import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { exportClientes, exportProveedores, exportFacturasProveedor } from '../utils/exportExcel';
import { useStore, today, calcLineas, formatEur } from '../store';
import Reparaciones, { EstadoReparacionBadge } from './Reparaciones';
import { Btn, Badge, Modal, Input, Select, Textarea, SearchBar, BotonLimpiar, useSearch, useLocalStorage, Confirm, StatCard, MultiSelect, ProveedorSelector, useSortable, SortableTh, EstadoQuick, useT, THEME, pastelBtn, matchArticuloPorCodigo } from './UI';

// ── CLIENTES ────────────────────────────────────────────
export function Clientes() {
  const theme = useT();
  const { data, addItem, replaceItem, deleteItem } = useStore();
  const { clientes, tickets, facturas, reparaciones } = data;
  const { q, setQ, filtered } = useSearch(clientes, ['nombre','nif','telefono','email'], 'clientes');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const newClient = () => ({ id:null, nombre:'', nif:'', telefono:'', email:'', calle:'', cp:'', localidad:'', provincia:'', direccion:'' });
  const upd = (f,v) => setForm(x=>({...x,[f]:v}));

  // Editar un cliente SOLO actualiza su ficha — los documentos ya emitidos
  // (facturas, tickets, presupuestos...) conservan el snapshot de datos del
  // cliente que tenían al crearse, no se reescriben retroactivamente.
  function save() {
    if (!form.nombre) return alert('El nombre es obligatorio.');
    const direccion = [form.calle, form.cp, form.localidad, form.provincia].filter(Boolean).join(', ');
    const cliente = { ...form, direccion };
    if (form.id) replaceItem('clientes', cliente);
    else addItem('clientes', { ...cliente, id: Date.now() });
    setModal(null);
  }

  // useMemo — evita recalcular los contadores (O(clientes × facturas+tickets+reparaciones))
  // en cada tecla escrita en el formulario del modal; solo recalcula si cambian los datos reales.
  const filteredConContadores = useMemo(() => filtered.map(c => ({
    ...c,
    _nFacturas:     facturas.filter(f => String(f.clienteId) === String(c.id)).length,
    _nTickets:      tickets.filter(t => String(t.clienteId) === String(c.id)).length,
    _nReparaciones: reparaciones.filter(r => String(r.clienteId) === String(c.id)).length,
  })), [filtered, facturas, tickets, reparaciones]);
  const { sorted: sortedClientes, sortField: sfC, sortDir: sdC, handleSort: hsC } = useSortable(filteredConContadores);

  return (
    <div>
      <SearchBar value={q} onChange={setQ} placeholder="Buscar cliente, NIF, teléfono...">
        <Btn variant="ghost" title="Exportar clientes a Excel" onClick={() => exportClientes(sortedClientes)}>📊 Exportar Excel</Btn>
        <Btn variant="primary" title="Crear nuevo cliente" onClick={() => { setForm(newClient()); setModal('form'); }}>+ Nuevo cliente</Btn>
      </SearchBar>
      <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:theme.surface2 }}>
              {[['Nombre / Empresa','nombre'],['NIF/CIF','nif'],['Teléfono','telefono'],['Email','email'],['Facturas','_nFacturas'],['Tickets','_nTickets'],['Reparac.','_nReparaciones'],[''  ,null]].map(([label,field]) => (
                <SortableTh key={label+field} label={label} field={field} sortField={sfC} sortDir={sdC} onSort={hsC} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedClientes.map((c, i) => (
              <tr key={c.id} style={{ borderBottom:`1px solid ${theme.border}`, background: i%2 ? theme.surface2 : 'transparent' }}>
                <td style={{ padding:'10px 14px', fontWeight:500 }}>{c.nombre}</td>
                <td style={{ padding:'10px 14px', color:theme.muted }}>{c.nif||'—'}</td>
                <td style={{ padding:'10px 14px' }}>{c.telefono||'—'}</td>
                <td style={{ padding:'10px 14px', color:theme.muted }}>{c.email||'—'}</td>
                <td style={{ padding:'10px 14px' }}><Badge variant="blue">{c._nFacturas}</Badge></td>
                <td style={{ padding:'10px 14px' }}><Badge variant="gray">{c._nTickets}</Badge></td>
                <td style={{ padding:'10px 14px' }}><Badge variant="purple">{c._nReparaciones}</Badge></td>
                <td style={{ padding:'10px 14px' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <Btn size="sm" variant="ghost" title="Editar" onClick={() => { setForm({...c}); setModal('form'); }}>✏️</Btn>
                    <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} title="Eliminar" onClick={() => setConfirm(c.id)}>🗑️</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={8} style={{ padding:30, textAlign:'center', color:theme.faint }}>No hay clientes</td></tr>}
          </tbody>
        </table>
      </div>
      {modal === 'form' && form && (
        <Modal title={form.id ? 'Editar cliente' : 'Nuevo cliente'} onClose={() => setModal(null)} width={500}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
            <div style={{ gridColumn:'1/-1' }}><Input label="Nombre / Razón social" value={form.nombre} onChange={v=>upd('nombre',v)} /></div>
            <Input label="NIF / CIF" value={form.nif} onChange={v=>upd('nif',v)} />
            <Input label="Teléfono" value={form.telefono} onChange={v=>upd('telefono',v)} />
            <Input label="Email" value={form.email} onChange={v=>upd('email',v)} type="email" />
            <div style={{ gridColumn:'1/-1' }}><Input label="Calle" value={form.calle||''} onChange={v=>upd('calle',v)} placeholder="C/ Rodriguez San Pedro 31" /></div>
            <Input label="Código postal" value={form.cp||''} onChange={v=>upd('cp',v)} placeholder="28015" />
            <Input label="Localidad" value={form.localidad||''} onChange={v=>upd('localidad',v)} placeholder="Madrid" />
            <div style={{ gridColumn:'1/-1' }}><Input label="Provincia" value={form.provincia||''} onChange={v=>upd('provincia',v)} placeholder="Madrid" /></div>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={() => setModal(null)} title="Descartar cambios">Cancelar</Btn>
            <Btn variant="primary" onClick={save} title="Guardar">Guardar</Btn>
          </div>
        </Modal>
      )}
      {confirm && <Confirm msg="¿Eliminar este cliente?" onOk={() => { deleteItem('clientes', confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ── ESPACIOS DE TALLER (boxes/estanterías) ────────────────
// Estado de cada espacio NUNCA se guarda: se calcula al vuelo mirando qué
// reparación activa tiene lugar === nombre del espacio. "Fuera de servicio"
// es la única marca manual, y una reparación activa manda sobre ella.
// Color del tablero de espacios según el estado de la reparación que lo ocupa.
const LEYENDA_ESPACIOS = [
  ['libre', 'Libre'], ['pendiente', 'Pendiente'], ['enCurso', 'En curso'], ['piezas', 'Esperando piezas'],
  ['otro', 'Otro'], ['listo', 'Listo para recoger'], ['sinReparacion', 'Sin reparación'], ['fuera', 'Fuera de servicio'],
];

function claseEspacio(estado, rep) {
  if (estado === 'libre') return 'libre';
  if (estado === 'fuera') return 'fuera';
  if (estado === 'ocupadoManual') return 'otro';
  switch (rep && rep.estado) {
    case 'Recibido': return 'pendiente';
    case 'En diagnóstico': case 'En reparación': return 'enCurso';
    case 'Esperando piezas': return 'piezas';
    case 'Reparado - Listo para recoger': return 'listo';
    case 'Sin reparación - Listo para recoger': return 'sinReparacion';
    default: return 'otro';
  }
}

function coloresEspacio(clase, dark, theme) {
  const c = (claro, oscuro) => (dark ? oscuro : claro);
  switch (clase) {
    case 'pendiente':     return { bg: c('#fecaca','rgba(239,68,68,0.40)'),  border:'#dc2626', text: c('#7f1d1d','#ffe4e6'), bw: 2 };
    case 'enCurso':       return { bg: c('#dbeafe','rgba(59,130,246,0.16)'), border:'#3b82f6', text: c('#1e40af','#93c5fd') };
    case 'piezas':        return { bg: c('#ede9fe','rgba(139,92,246,0.18)'), border:'#8b5cf6', text: c('#5b21b6','#c4b5fd') };
    case 'otro':          return { bg: c('#fae8ff','rgba(217,70,239,0.16)'), border:'#d946ef', text: c('#86198f','#f0abfc') };
    case 'listo':         return { bg: c('#dcfce7','rgba(16,185,129,0.14)'), border:'#10b981', text: c('#166534','#6ee7b7') };
    // Rojo "oscuro" con rayas diagonales y borde grueso: se distingue de Pendiente de un vistazo
    case 'sinReparacion': return { bg: c('#4c0519','#0d0206'), border: c('#be123c','#fb7185'), text: '#ffe4e6',
      bw: 2, img: `repeating-linear-gradient(135deg, ${c('rgba(251,113,133,0.32)','rgba(251,113,133,0.45)')} 0 7px, transparent 7px 14px)` };
    case 'fuera':         return { bg: c('#e5e7eb','rgba(148,163,184,0.18)'), border: theme.borderStrong, text: theme.faint };
    default:              return { bg: theme.surface, border: c('#cbd5e1','#475569'), text: theme.muted }; // libre: blanco (superficie del tema) con borde fino
  }
}

export function EspaciosTaller() {
  const theme = useT();
  const { data, addItem, deleteItem, replaceItem } = useStore();
  const { espaciosTaller = [], reparaciones = [] } = data;
  const [nombre, setNombre] = useState('');
  const [zona, setZona] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [busca, setBusca] = useState('');
  const [editarId, setEditarId] = useState(null);
  const [ticketId, setTicketId] = useState(null);

  const ocupacion = useMemo(() => {
    const m = new Map();
    reparaciones.filter(r => r.estado !== 'Entregado' && r.lugar).forEach(r => m.set(r.lugar, r));
    return m;
  }, [reparaciones]);

  // Reparaciones activas cuyo lugar se escribió a mano (no es un espacio dado de alta): van al final del tablero.
  const otrosLugares = useMemo(() => {
    const nombres = new Set(espaciosTaller.map(e => e.nombre));
    return reparaciones
      .filter(r => r.estado !== 'Entregado' && r.lugar && String(r.lugar).trim() && !nombres.has(r.lugar))
      .sort((x, y) => String(x.lugar).localeCompare(String(y.lugar), 'es', { numeric: true }) || String(x.num).localeCompare(String(y.num), 'es', { numeric: true }))
      .map(r => ({ id: 'otro-' + r.id, nombre: r.lugar, zona: 'Otros lugares', fueraDeServicio: false, ocupacionManual: '', ocupacionManualEstado: '', _rep: r }));
  }, [reparaciones, espaciosTaller]);

  // Búsqueda por cliente, nº de reparación o Serie Root: atenúa lo que no coincide en vez de ocultarlo,
  // para no descolocar la rejilla del tablero.
  const norm = s => (s ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const qn = norm(busca.trim());
  const coincide = e => {
    if (!qn) return false;
    const r = e._rep || ocupacion.get(e.nombre);
    return !!r && (norm(r.clienteNombre).includes(qn) || norm(r.num).includes(qn) || norm(r.serieRoot).includes(qn));
  };
  const coincidencias = qn ? [...espaciosTaller, ...otrosLugares].filter(coincide).map(e => e.nombre).sort((a, b) => a.localeCompare(b, 'es', { numeric: true })) : [];

  function crear() {
    const n = nombre.trim();
    if (!n) return alert('Escribe un nombre para el espacio.');
    if (espaciosTaller.some(e => e.nombre.toLowerCase() === n.toLowerCase())) return alert('Ya existe un espacio con ese nombre.');
    addItem('espaciosTaller', { id: Date.now(), nombre: n, zona: zona.trim(), fueraDeServicio: false, ocupacionManual: '', ocupacionManualEstado: '' });
    setNombre(''); setZona('');
  }

  const grupos = useMemo(() => {
    const m = new Map();
    espaciosTaller.forEach(e => {
      const z = e.zona || 'Sin zona';
      if (!m.has(z)) m.set(z, []);
      m.get(z).push(e);
    });
    const grupos = [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'es'));
    grupos.forEach(([, espacios]) => espacios.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { numeric:true })));
    return grupos;
  }, [espaciosTaller]);

  const renderEspacio = e => {
              const rep = e._rep || ocupacion.get(e.nombre);
              // Prioridad: reparación activa real > ocupación manual > fuera de servicio > libre.
              const estado = rep ? 'ocupado' : e.ocupacionManual ? 'ocupadoManual' : e.fueraDeServicio ? 'fuera' : 'libre';
              const clase = claseEspacio(estado, rep);
              const colores = coloresEspacio(clase, theme===THEME.dark, theme);

              function ocuparLibremente() {
                const texto = window.prompt('¿Con qué ocupas este espacio?', e.ocupacionManual || '');
                if (texto === null) return;
                const estadoManual = window.prompt('¿Estado? (opcional)', e.ocupacionManualEstado || '');
                const estadoFinal = estadoManual === null ? (e.ocupacionManualEstado || '') : estadoManual.trim();
                replaceItem('espaciosTaller', { ...e, ocupacionManual: texto.trim(), ocupacionManualEstado: estadoFinal });
              }

              return (
                <div key={e.id} id={e._rep ? 'esp-otro-' + e.id : 'esp-' + e.nombre.replace(/\s+/g, '-')} data-lugar={e._rep ? e.nombre : undefined}
                  style={{ position:'relative', borderRadius:10, padding:'12px 14px', background:colores.bg, backgroundImage:colores.img, border:`${colores.bw||1}px ${colores.bs||'solid'} ${colores.border}`, minHeight:70,
                    transition:'opacity .15s, box-shadow .15s', ...(qn ? (coincide(e) ? { boxShadow:`0 0 0 3px ${theme.primary}` } : { opacity:0.25 }) : {}) }}>
                  {!e._rep && (
                  <button type="button" onClick={ev => { ev.stopPropagation(); setConfirm(e.id); }}
                    style={{ position:'absolute', top:4, right:6, background:'none', border:'none', color:colores.text, opacity:0.6, cursor:'pointer', fontSize:12 }} title="Borrar espacio">✕</button>
                  )}
                  <div style={{ fontWeight:700, fontSize:14, color:colores.text }}>{e.nombre}</div>
                  {estado === 'ocupado' && rep && (
                    <>
                      <div style={{ fontSize:11, color:colores.text, marginTop:4 }}>{rep.num} · {rep.clienteNombre}<br/>{rep.equipo}</div>
                      {rep.averia && <div style={{ fontSize:10, color:colores.text, opacity:0.85, marginTop:2 }}>⚠️ {rep.averia}</div>}
                      <div style={{ marginTop:4 }} onClick={ev => ev.stopPropagation()}>
                        <EstadoReparacionBadge r={rep} onChange={nuevoEstado => replaceItem('reparaciones', { ...rep, estado: nuevoEstado, ...(nuevoEstado === 'Entregado' ? { fechaEntrega: rep.fechaEntrega || today(), lugar: '' } : {}) })} />
                      </div>
                      {rep.notas && <div style={{ fontSize:10, color:colores.text, opacity:0.8, marginTop:2, fontStyle:'italic' }}>📝 {rep.notas}</div>}
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
                        <button type="button" onClick={ev => {
                          ev.stopPropagation();
                          setEditarId(rep.id);
                        }} style={{ padding:'3px 8px', borderRadius:6, border:`1px solid ${colores.border}`, background:'none', color:colores.text, fontSize:11, cursor:'pointer' }}>
                          ✏️ Editar reparación
                        </button>
                        <button type="button" onClick={ev => {
                          ev.stopPropagation();
                          setTicketId(rep.id);
                        }} style={{ padding:'3px 8px', borderRadius:6, border:`1px solid ${colores.border}`, background:'none', color:colores.text, fontSize:11, cursor:'pointer' }}>
                          📋 Ticket
                        </button>
                        <button type="button" title="Marcar como entregada y liberar el espacio" onClick={ev => {
                          ev.stopPropagation();
                          replaceItem('reparaciones', { ...rep, estado: 'Entregado', fechaEntrega: rep.fechaEntrega || today(), lugar: '' });
                        }} style={{ padding:'3px 10px', borderRadius:6, border:'1px solid #059669', background:'#059669', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                          📦 Entregar
                        </button>
                      </div>
                    </>
                  )}
                  {estado === 'ocupadoManual' && (
                    <>
                      <div style={{ fontSize:11, color:colores.text, marginTop:4 }}>{e.ocupacionManual}</div>
                      {e.ocupacionManualEstado && <div style={{ fontSize:10, color:colores.text, opacity:0.8, marginTop:2 }}>{e.ocupacionManualEstado}</div>}
                      <button type="button" onClick={ocuparLibremente}
                        style={{ marginTop:6, marginRight:6, padding:'3px 8px', borderRadius:6, border:`1px solid ${colores.border}`, background:'none', color:colores.text, fontSize:11, cursor:'pointer' }}>
                        ✏️ Editar
                      </button>
                      <button type="button" onClick={() => replaceItem('espaciosTaller', { ...e, ocupacionManual: '', ocupacionManualEstado: '' })}
                        style={{ marginTop:6, padding:'3px 8px', borderRadius:6, border:`1px solid ${colores.border}`, background:'none', color:colores.text, fontSize:11, cursor:'pointer' }}>
                        🔓 Liberar
                      </button>
                    </>
                  )}
                  {estado === 'fuera' && (
                    <>
                      <div style={{ fontSize:11, color:colores.text, marginTop:4 }}>Fuera de servicio</div>
                      <button type="button" onClick={() => replaceItem('espaciosTaller', { ...e, fueraDeServicio: false })}
                        style={{ marginTop:6, padding:'3px 8px', borderRadius:6, border:`1px solid ${colores.border}`, background:'none', color:colores.text, fontSize:11, cursor:'pointer' }}>
                        ✅ Poner libre
                      </button>
                    </>
                  )}
                  {estado === 'libre' && (
                    <>
                      <div style={{ fontSize:11, color:colores.text, marginTop:4 }}>Libre</div>
                      <div style={{ display:'flex', gap:4, marginTop:6, flexWrap:'wrap' }}>
                        <button type="button" onClick={ocuparLibremente}
                          style={{ padding:'3px 8px', borderRadius:6, border:`1px solid ${colores.border}`, background:'none', color:colores.text, fontSize:11, cursor:'pointer' }}>
                          🔒 Ocupar
                        </button>
                        <button type="button" onClick={() => replaceItem('espaciosTaller', { ...e, fueraDeServicio: true })}
                          style={{ padding:'3px 8px', borderRadius:6, border:`1px solid ${colores.border}`, background:'none', color:colores.text, fontSize:11, cursor:'pointer' }}>
                          🚫 Fuera de servicio
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
  };

  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap', alignItems:'flex-end' }}>
        <Input label="Nombre del espacio nuevo" value={nombre} onChange={setNombre} placeholder="Ej: BOX 11, EST C2..." />
        <Input label="Zona (opcional, agrupa en el tablero)" value={zona} onChange={setZona} placeholder="Ej: BOX, EST..." />
        <Btn variant="primary" onClick={crear}>+ Añadir espacio</Btn>
      </div>
      {!espaciosTaller.length && <div style={{ padding:30, textAlign:'center', color:theme.faint }}>No hay espacios dados de alta todavía.</div>}
      {!!espaciosTaller.length && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
          {LEYENDA_ESPACIOS.map(([clave, etiqueta]) => {
            const col = coloresEspacio(clave, theme===THEME.dark, theme);
            return (
              <span key={clave} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:11, color:theme.muted }}>
                <span style={{ width:16, height:16, borderRadius:4, background:col.bg, backgroundImage:col.img, border:`${col.bw||1}px ${col.bs||'solid'} ${col.border}` }} />
                {etiqueta}
              </span>
            );
          })}
        </div>
      )}
      {!!espaciosTaller.length && (
        <div style={{ marginBottom:16 }}>
          <SearchBar value={busca} onChange={setBusca} placeholder="Buscar por cliente, nº de reparación o Serie Root..." />
          {qn && (
            <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:6, fontSize:12, color:theme.muted }}>
              {coincidencias.length === 0
                ? <span>Sin coincidencias en los espacios.</span>
                : <>
                    <span>{coincidencias.length} {coincidencias.length === 1 ? 'coincidencia' : 'coincidencias'}:</span>
                    {coincidencias.map(n => (
                      <button key={n} type="button"
                        onClick={() => { const el = document.getElementById('esp-' + n.replace(/\s+/g, '-')) || document.querySelector('[id^="esp-otro-"][data-lugar="' + n + '"]'); if (el) el.scrollIntoView({ behavior:'smooth', block:'center' }); }}
                        style={{ padding:'2px 10px', borderRadius:20, border:`1px solid ${theme.primary}`, background:theme.primaryBg, color:theme.primary, fontSize:11, fontWeight:600, cursor:'pointer' }}>
                        {n}
                      </button>
                    ))}
                  </>}
            </div>
          )}
        </div>
      )}
      {grupos.map(([zonaNombre, espacios]) => (
        <div key={zonaNombre} style={{ marginBottom:22 }}>
          <div style={{ fontSize:12, fontWeight:700, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:8 }}>{zonaNombre}</div>
          {/* BOX se lee en columnas (01,02...06 en la primera, 07-12 en la segunda...);
              el resto de zonas en filas normales de 3 — mismo nº de columnas para que
              ambos tipos de rejilla midan lo mismo de ancho. */}
          <div style={zonaNombre === 'BOX'
            ? { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gridTemplateRows:'repeat(6, auto)', gridAutoFlow:'column', gap:10 }
            : { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10 }}>
            {espacios.map(renderEspacio)}
          </div>
        </div>
      ))}
      {otrosLugares.length > 0 && (
        <div style={{ marginTop:28 }}>
          <div style={{ borderTop:`1px solid ${theme.borderStrong}`, marginBottom:14 }} />
          <div style={{ fontSize:12, fontWeight:700, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:8 }}>
            Otros lugares <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0 }}>— reparaciones fuera de los espacios dados de alta</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10 }}>
            {otrosLugares.map(renderEspacio)}
          </div>
        </div>
      )}
      {editarId && <Reparaciones abrirId={editarId} onCerrar={() => setEditarId(null)} />}
      {ticketId && <Reparaciones abrirTicketId={ticketId} onCerrar={() => setTicketId(null)} />}
      {confirm && <Confirm msg="¿Eliminar este espacio?" onOk={() => { deleteItem('espaciosTaller', confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ── ARTÍCULOS ────────────────────────────────────────────
function StockStepper({ value, onDelta, variant }) {
  const theme = useT();
  const btnStyle = { width:20, height:20, display:'flex', alignItems:'center', justifyContent:'center', border:`1px solid ${theme.borderStrong}`, borderRadius:5, background:theme.surface2, color:theme.body, fontSize:13, fontWeight:700, cursor:'pointer', lineHeight:1, padding:0 };
  return (
    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
      <button type="button" onClick={() => onDelta(-1)} style={btnStyle} title="Restar 1">−</button>
      {variant
        ? <Badge variant={variant}>{value}</Badge>
        : <span style={{ minWidth:18, textAlign:'center', color:theme.muted, fontSize:13 }}>{value}</span>}
      <button type="button" onClick={() => onDelta(1)} style={btnStyle} title="Sumar 1">+</button>
    </div>
  );
}

// Envía un trabajo de impresión a la Brother QL (reutilizado por Articulos y ArticuloFormModal)
function enviarEtiquetaQL(payload, setImprimiendo) {
  setImprimiendo(true);
  fetch('/api/etiqueta-ql', { method:'POST', headers:{ Authorization:`Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type':'application/json' },
    body: JSON.stringify(payload) })
    .then(r=>r.json()).then(d=>{ if(!d.ok) alert('Error: '+d.error); })
    .catch(e=>alert('Error: '+e.message))
    .finally(()=>setImprimiendo(false));
}

// A partir de una línea de factura de proveedor (ref/desc/cant/precio/iva), construye el form
// para el modal de artículo: si la ref coincide con un artículo existente, precarga edición
// (sumando el stock recibido); si no, precarga alta de artículo nuevo.
export function construirFormArticuloDesdeLinea(linea, articulos) {
  const ref = (linea.ref || '').trim();
  const precioCompra = parseFloat(linea.precio)||0;
  const cant = parseInt(linea.cant)||0;
  if (ref) {
    const existente = (articulos||[]).find(a => (a.ref||'').trim().toLowerCase() === ref.toLowerCase());
    if (existente) return { ...existente, precioCompra, _cantidadSugerida: cant };
  }
  return { id:null, ref, nombre: linea.desc || '', barcode:'', precio: precioCompra, precioCompra, ivaIncl:true, iva: linea.iva!=null ? +linea.iva : 21, stock: cant, stockMin: 5, _cantidadSugerida: cant };
}

// Modal de alta/edición de artículo — reutilizable (Articulos y "Revisar" de líneas de factura)
export function ArticuloFormModal({ form, setForm, onSave, onClose }) {
  const theme = useT();
  const [copias, setCopias] = useState(1);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [cantAdd, setCantAdd] = useState(form._cantidadSugerida ?? 1);

  const upd = (f,v) => setForm(x=>({...x,[f]:v}));
  const updPrecio = (v) => setForm(x => {
    const num = parseFloat(v) || 0;
    return { ...x, precio: x.ivaIncl ? num / (1 + (+x.iva||0)/100) : num };
  });
  const updIva = (v) => setForm(x => {
    const newIva = +v;
    if (x.ivaIncl) {
      const bruto = x.precio * (1 + (+x.iva||0)/100);
      return { ...x, iva: newIva, precio: bruto / (1 + newIva/100) };
    }
    return { ...x, iva: newIva };
  });
  const precioMostrado = (x) => x?.ivaIncl ? Math.round(x.precio * (1 + (+x.iva||0)/100) * 100) / 100 : x?.precio;

  function guardar() {
    if (!form.nombre) return alert('El nombre es obligatorio.');
    if (!form.ref) return alert('La referencia es obligatoria.');
    onSave(form);
  }

  return (
    <Modal title={form.id ? 'Editar artículo' : 'Nuevo artículo'} onClose={onClose} width={480}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
        <Input label="Referencia" value={form.ref} onChange={v=>upd('ref',v)} />
        <div style={{ position:'relative' }}>
          <Input label="Código de barras (EAN/UPC)" value={form.barcode||''} onChange={v=>upd('barcode',v)} placeholder="Escanea o escribe el código" />
          <span style={{ position:'absolute', right:10, bottom:8, fontSize:16, color:theme.faint, pointerEvents:'none' }}>▦</span>
        </div>
        <div style={{ gridColumn:'1/-1' }}><Input label="Descripción" value={form.nombre} onChange={v=>upd('nombre',v)} /></div>
        <Input label="Precio compra (€)" value={form.precioCompra||0} onChange={v=>upd('precioCompra', parseFloat(v)||0)} type="number" step="0.01" />
        <Input label="Precio venta (€)" value={precioMostrado(form)} onChange={updPrecio} type="number" step="0.01" />
        <Select label="IVA %" value={String(form.iva)} onChange={updIva} options={['0','4','10','21'].map(v=>({value:v,label:`${v}%`}))} />
        {form.precioCompra > 0 && form.precio > 0 && form.precioCompra >= form.precio && (
          <div style={{ gridColumn:'1/-1', background:'#fee2e2', color:'#991b1b', borderRadius:8, padding:'8px 12px', fontSize:12, fontWeight:600 }}>
            ⚠️ El precio de compra ({form.precioCompra.toFixed(2)}€) es igual o mayor que el de venta ({form.precio.toFixed(2)}€) — sin margen o con pérdidas.
          </div>
        )}
        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          <label style={{ fontSize:11, fontWeight:600, color:'transparent', textTransform:'uppercase', letterSpacing:'.04em' }}>.</label>
          <div style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 0' }}>
            <input type="checkbox" id="art-ivaincl" checked={!!form.ivaIncl} onChange={e=>upd('ivaIncl', e.target.checked)} />
            <label htmlFor="art-ivaincl" style={{ fontSize:12, color:theme.body, cursor:'pointer' }} title="El precio introducido ya incluye el IVA">IVA incl.</label>
          </div>
        </div>
        <Input label="Stock actual" value={form.stock} onChange={v=>upd('stock',v)} type="number" />
        <Input label="Stock mínimo" value={form.stockMin} onChange={v=>upd('stockMin',v)} type="number" />
        {form.pedidoPendiente > 0 && (
          <div style={{ gridColumn:'1/-1', fontSize:12, color:'#b45309', fontWeight:600, marginTop:-6, display:'flex', alignItems:'center', gap:8 }}>
            <span>🕒 {form.pedidoPendiente} pedidas a proveedor (pendientes de recibir)</span>
            <button type="button" title="Marcar como recibido" onClick={() => setForm(f => ({ ...f, pedidoPendiente: 0 }))}
              style={{ border:'1px solid #b45309', background:'transparent', color:'#b45309', borderRadius:6, padding:'2px 6px', fontSize:11, fontWeight:600, cursor:'pointer' }}>
              ✓ Marcar recibido
            </button>
          </div>
        )}
        {form._cantidadSugerida != null && (
          <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'flex-end', gap:8 }}>
            <Input label="Cantidad recibida" value={cantAdd} onChange={v=>setCantAdd(parseInt(v)||0)} type="number" min="0" style={{ width:100 }} />
            <Btn variant="outline" onClick={() => {
              setForm(f => ({ ...f, stock: (parseInt(f.stock)||0) + cantAdd, pedidoPendiente: Math.max(0, (f.pedidoPendiente||0) - cantAdd) }));
              if (cantAdd > 0 && window.confirm(`¿Imprimir ${cantAdd} etiqueta${cantAdd!==1?'s':''} con precio?`)) {
                enviarEtiquetaQL({ tipo:'articulo', desc: form.nombre, precio: form.precio, iva: form.iva, copias: cantAdd }, setImprimiendo);
              }
            }} title="Suma esta cantidad al stock actual">➕ Añadir stock</Btn>
          </div>
        )}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, borderTop:`1px solid ${theme.border}`, paddingTop:12, marginBottom:14 }}>
        <span style={{ fontSize:12, color:theme.muted, fontWeight:600 }}>🔖 Etiqueta Brother QL-600</span>
        <Input label="Copias" value={copias} onChange={v=>setCopias(Math.max(1, parseInt(v)||1))} type="number" min="1" style={{ width:70 }} />
        <Btn variant="outline" disabled={imprimiendo} onClick={() => enviarEtiquetaQL({ tipo:'articulo', desc: form.nombre, precio: form.precio, iva: form.iva, copias }, setImprimiendo)} title="Imprimir etiqueta con precio">
          {imprimiendo ? 'Imprimiendo…' : 'Precio'}
        </Btn>
        <Btn variant="outline" disabled={imprimiendo} onClick={() => enviarEtiquetaQL({ tipo:'articulo-ref', desc: form.ref, copias }, setImprimiendo)} title="Imprimir etiqueta solo referencia">
          {imprimiendo ? 'Imprimiendo…' : 'Referencia'}
        </Btn>
      </div>
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
        <Btn variant="outline" onClick={onClose} title="Descartar cambios">Cancelar</Btn>
        <Btn variant="primary" onClick={guardar} title="Guardar">Guardar</Btn>
      </div>
    </Modal>
  );
}

export function Articulos() {
  const theme = useT();
  const { data, addItem, replaceItem, deleteItem } = useStore();
  const { articulos } = data;
  const { q, setQ, filtered } = useSearch(articulos, ['ref','nombre','barcode'], 'articulos');
  const [soloStockBajo, setSoloStockBajo] = useLocalStorage('articulos_soloStockBajo', false);
  const filteredStock = soloStockBajo ? filtered.filter(a => a.stock <= a.stockMin) : filtered;
  const { sorted: sortedArticulos, sortField: sfA, sortDir: sdA, handleSort: hsA } = useSortable(filteredStock);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [modalPedido, setModalPedido] = useState(false);
  const [lineasPedido, setLineasPedido] = useState([]);
  const [busquedaPedido, setBusquedaPedido] = useState('');
  const [escaneandoPedido, setEscaneandoPedido] = useState(false);
  const ultimoEscaneoPedido = useRef(0);

  const stockBajo = articulos.filter(a => a.stock <= a.stockMin);

  function pedirCopias() {
    const n = parseInt(window.prompt('¿Cuántas copias quieres imprimir?', '1'), 10);
    return (n && n > 0) ? n : null;
  }

  function abrirModalPedido() {
    setLineasPedido(stockBajo.map(a => ({ id: a.id, ref: a.ref, nombre: a.nombre, cantidad: Math.max(1, a.stockMin - a.stock) })));
    setBusquedaPedido('');
    setModalPedido(true);
  }
  function agregarLineaPedido(a) {
    setLineasPedido(ls => {
      const idx = ls.findIndex(l => l.id === a.id);
      if (idx >= 0) return ls.map((l, i) => i === idx ? { ...l, cantidad: l.cantidad + 1 } : l);
      return [...ls, { id: a.id, ref: a.ref, nombre: a.nombre, cantidad: 1 }];
    });
    setBusquedaPedido('');
  }
  function quitarLineaPedido(i) {
    setLineasPedido(ls => ls.filter((_, idx) => idx !== i));
  }
  const sugerenciasPedido = busquedaPedido.trim() ? articulos.filter(a => {
    const qb = busquedaPedido.toLowerCase();
    return (a.nombre||'').toLowerCase().includes(qb) || (a.ref||'').toLowerCase().includes(qb) || (a.barcode||'').toLowerCase().includes(qb);
  }).slice(0, 8) : [];
  function descargarCsvPedido() {
    const filas = lineasPedido.map(l => `${l.ref};${l.cantidad}`);
    const csv = ['Codigo;Unidades', ...filas].join('\r\n') + '\r\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pedido_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    lineasPedido.forEach(l => {
      const art = articulos.find(a => a.id === l.id);
      if (art) replaceItem('articulos', { ...art, pedidoPendiente: (art.pedidoPendiente||0) + l.cantidad });
    });
  }
  function imprimirEtiqueta(art, n) {
    enviarEtiquetaQL({ tipo:'articulo', desc: art.nombre, precio: art.precio, iva: art.iva, copias: n }, setImprimiendo);
  }
  function imprimirEtiquetaRef(art, n) {
    enviarEtiquetaQL({ tipo:'articulo-ref', desc: art.ref, copias: n }, setImprimiendo);
  }

  function guardarArticulo(finalForm) {
    if (finalForm.id) replaceItem('articulos', finalForm);
    else addItem('articulos', { ...finalForm, id: Date.now(), precioCompra:+finalForm.precioCompra||0, precio:+finalForm.precio||0, iva:+finalForm.iva||21, stock:+finalForm.stock||0, stockMin:+finalForm.stockMin||0 });
    setModal(null);
  }

  function newArt() {
    setForm({ id:null, ref:'', nombre:'', barcode:'', precioCompra:0, precio:0, ivaIncl:true, iva:21, stock:0, stockMin:5 });
    setModal('form');
  }

  return (
    <div>
      {stockBajo.length > 0 && (
        <div style={{ background:'#fef3c7', border:'1px solid #fbbf24', borderRadius:10, padding:'10px 14px', marginBottom:12, fontSize:13, color:'#92400e', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
          <span>⚠️ <b>{stockBajo.length} artículo{stockBajo.length!==1?'s':''}</b> con stock bajo o agotado: {stockBajo.map((a, i) => (
            <span key={a.id}>
              <span onClick={() => { setForm({...a}); setModal('form'); }} title="Abrir artículo" style={{ cursor:'pointer', textDecoration:'underline', fontWeight:600 }}>{a.ref}</span>
              {i < stockBajo.length - 1 ? ', ' : ''}
            </span>
          ))}</span>
          <Btn size="sm" variant="outline" onClick={abrirModalPedido} title="Revisar y generar pedido">Revisar</Btn>
        </div>
      )}
      <SearchBar value={q} onChange={setQ} placeholder="Buscar artículo, referencia...">
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:theme.body, cursor:'pointer', whiteSpace:'nowrap' }}>
          <input type="checkbox" checked={soloStockBajo} onChange={e=>setSoloStockBajo(e.target.checked)} />
          Solo sin stock / stock bajo
        </label>
        <Btn variant="outline" onClick={abrirModalPedido} title="Crear un pedido a proveedor a mano">📋 Hacer pedido</Btn>
        <Btn variant="primary" onClick={newArt} title="Crear nuevo artículo">+ Nuevo artículo</Btn>
      </SearchBar>
      <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:theme.surface2 }}>
              {[['Ref.','ref'],['Cód.Barras','barcode'],['Descripción','nombre'],['PVP','precio'],['IVA','iva'],['PVP c/IVA',null],['Stock','stock'],['Stock mín.','stockMin'],[''  ,null]].map(([label,field]) => (
                <SortableTh key={label+field} label={label} field={field} sortField={sfA} sortDir={sdA} onSort={hsA} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedArticulos.map((a, i) => (
              <tr key={a.id} style={{ borderBottom:`1px solid ${theme.border}`, background:
                a.stock<=0 ? (theme===THEME.dark ? 'rgba(239,68,68,0.14)' : '#fef2f2')
                : a.stock<=a.stockMin ? (theme===THEME.dark ? 'rgba(245,158,11,0.14)' : '#fffbeb')
                : (i%2 ? theme.surface2 : 'transparent') }}>
                <td style={{ padding:'10px 14px', fontWeight:600, color:theme.body }}>{a.ref}</td>
                <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:12, color:theme.muted }}>{a.barcode||<span style={{color:theme.faint}}>—</span>}</td>
                <td style={{ padding:'10px 14px' }}>{a.nombre}</td>
                <td style={{ padding:'10px 14px', fontWeight:500 }}>{a.precio?.toFixed(2)}€</td>
                <td style={{ padding:'10px 14px', color:theme.muted }}>{a.iva}%</td>
                <td style={{ padding:'10px 14px', fontWeight:500 }}>{(a.precio * (1 + (a.iva||0)/100)).toFixed(2)}€</td>
                <td style={{ padding:'10px 14px' }}>
                  <StockStepper value={a.stock} variant={a.stock<=0 ? 'red' : a.stock<=a.stockMin ? 'amber' : 'green'}
                    onDelta={d => replaceItem('articulos', { ...a, stock: Math.max(0, (parseInt(a.stock)||0) + d), pedidoPendiente: d > 0 ? Math.max(0, (a.pedidoPendiente||0) - d) : (a.pedidoPendiente||0) })} />
                  {a.pedidoPendiente > 0 && (
                    <div style={{ marginTop:4, display:'flex', alignItems:'center', gap:4, fontSize:11, color:theme.muted, whiteSpace:'nowrap' }}>
                      <span>🕒 {a.pedidoPendiente} pedidas</span>
                      <button type="button" title="Marcar como recibido" onClick={() => replaceItem('articulos', { ...a, pedidoPendiente: 0 })}
                        style={{ background:'none', border:'none', color:'#10b981', cursor:'pointer', fontSize:12, padding:0 }}>✓</button>
                    </div>
                  )}
                </td>
                <td style={{ padding:'10px 14px' }}>
                  <StockStepper value={a.stockMin}
                    onDelta={d => replaceItem('articulos', { ...a, stockMin: Math.max(0, (parseInt(a.stockMin)||0) + d) })} />
                </td>
                <td style={{ padding:'10px 14px' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <Btn size="sm" variant="ghost" title="Editar" onClick={() => { setForm({...a}); setModal('form'); }}>✏️</Btn>
                    <Btn size="sm" variant="ghost" title="Etiqueta con precio" onClick={() => { const n = pedirCopias(); if (n) imprimirEtiqueta(a, n); }}>🔖</Btn>
                    <Btn size="sm" variant="ghost" title="Etiqueta solo referencia" onClick={() => { const n = pedirCopias(); if (n) imprimirEtiquetaRef(a, n); }}>🏷️</Btn>
                    <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} title="Eliminar" onClick={() => setConfirm(a.id)}>🗑️</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {!filteredStock.length && <tr><td colSpan={9} style={{ padding:30, textAlign:'center', color:theme.faint }}>No hay artículos</td></tr>}
          </tbody>
        </table>
      </div>
      {modal === 'form' && form && (
        <ArticuloFormModal form={form} setForm={setForm} onClose={() => setModal(null)} onSave={guardarArticulo} />
      )}
      {confirm && <Confirm msg="¿Eliminar este artículo?" onOk={() => { deleteItem('articulos', confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />}
      {modalPedido && (
        <Modal title="Hacer pedido a proveedor" onClose={() => setModalPedido(null)} width={640}>
          <div style={{ position:'relative', marginBottom:14 }}>
            <input value={busquedaPedido}
              onChange={e => setBusquedaPedido(e.target.value)}
              readOnly={escaneandoPedido}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const ahora = Date.now();
                  if (ahora - ultimoEscaneoPedido.current < 500) return; // ignora doble lectura
                  const a = matchArticuloPorCodigo(articulos, busquedaPedido.trim());
                  if (a) {
                    agregarLineaPedido(a);
                    ultimoEscaneoPedido.current = ahora;
                    setEscaneandoPedido(true);
                    setTimeout(() => setEscaneandoPedido(false), 500);
                  }
                }
              }}
              placeholder="Nombre, referencia o escanea código de barras..."
              style={{ width:'100%', padding:'9px 12px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box', background: escaneandoPedido ? theme.surface2 : undefined }} />
            {escaneandoPedido && <span title="Leído, espera un momento..." style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', fontSize:16 }}>⏳</span>}
            {sugerenciasPedido.length > 0 && (
              <div style={{ position:'absolute', zIndex:20, top:'100%', left:0, right:0, marginTop:2, background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:8, boxShadow:'0 4px 14px rgba(0,0,0,0.15)', maxHeight:220, overflowY:'auto' }}>
                {sugerenciasPedido.map(a => (
                  <div key={a.id} onClick={() => agregarLineaPedido(a)}
                    style={{ padding:'7px 12px', cursor:'pointer', fontSize:13, borderBottom:`1px solid ${theme.border}` }}>
                    <div style={{ fontWeight:600, color:theme.body }}>{a.nombre}</div>
                    <div style={{ color:theme.faint, fontSize:11 }}>
                      {a.ref}{a.barcode ? ` · ${a.barcode}` : ''}
                      {a.pedidoPendiente > 0 && <span style={{ color:'#b45309', fontWeight:600 }}> · 🕒 ya pedidas {a.pedidoPendiente}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ background:theme.surface2, borderRadius:10, border:`1px solid ${theme.border}`, overflowX:'auto', marginBottom:14 }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:theme.surface2 }}>
                  <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:theme.muted, fontWeight:600 }}>Código</th>
                  <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:theme.muted, fontWeight:600 }}>Descripción</th>
                  <th style={{ padding:'8px 12px', textAlign:'right', fontSize:11, color:theme.muted, fontWeight:600 }}>Cant. a pedir</th>
                  <th style={{ padding:'8px 12px' }}></th>
                </tr>
              </thead>
              <tbody>
                {lineasPedido.map((l, i) => {
                  const yaPedido = articulos.find(a => a.id === l.id)?.pedidoPendiente || 0;
                  return (
                  <tr key={l.id} style={{ borderTop:`1px solid ${theme.border}`, background: i%2 ? theme.surface : 'transparent' }}>
                    <td style={{ padding:'8px 12px', fontWeight:600, color:theme.body }}>{l.ref}</td>
                    <td style={{ padding:'8px 12px', color:theme.body }}>
                      {l.nombre}
                      {yaPedido > 0 && <div style={{ fontSize:11, color:'#b45309', fontWeight:600 }}>🕒 ya había {yaPedido} pedidas</div>}
                    </td>
                    <td style={{ padding:'6px 12px' }}>
                      <div style={{ display:'flex', justifyContent:'flex-end' }}>
                        <StockStepper value={l.cantidad}
                          onDelta={d => setLineasPedido(ls => ls.map((x, idx) => idx === i ? { ...x, cantidad: Math.max(1, x.cantidad + d) } : x))} />
                      </div>
                    </td>
                    <td style={{ padding:'6px 8px', textAlign:'right' }}>
                      <Btn size="sm" variant="ghost" title="Quitar línea" onClick={() => quitarLineaPedido(i)}>🗑️</Btn>
                    </td>
                  </tr>
                  );
                })}
                {!lineasPedido.length && <tr><td colSpan={4} style={{ padding:20, textAlign:'center', color:theme.faint }}>Añade artículos buscando arriba</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={() => setModalPedido(false)} title="Cerrar">Cerrar</Btn>
            <Btn variant="primary" onClick={descargarCsvPedido} disabled={!lineasPedido.length} title="Descargar CSV (Codigo;Unidades)">⬇️ Descargar CSV</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── PEDIDOS ────────────────────────────────────────────
const PAGOS_PED = ['Pendiente','En tránsito','Recibido','Cancelado'];

export function Pedidos() {
  const theme = useT();
  const { data, addItem, replaceItem, deleteItem, nextNum } = useStore();
  const { pedidos, config, articulos, proveedores = [] } = data;
  const { q, setQ, filtered } = useSearch(pedidos, ['num','proveedor','estado'], 'pedidos');
  const { sorted: sortedPedidos, sortField: sfP, sortDir: sdP, handleSort: hsP } = useSortable(filtered, 'num', 'desc');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(null);
  const [confirm, setConfirm] = useState(null);

  function newPed() { setForm({ id:null, num:'', fecha:today(), proveedorId:'', proveedor:'', lineas:[{desc:'',cant:1,precio:0,iva:21}], estado:'Pendiente', notas:'' }); setModal('form'); }
  function handleProveedorChange({ id, nombre }) { setForm(f => ({ ...f, proveedorId: id, proveedor: nombre })); }
  function handleNuevoProveedor(nuevo) { addItem('proveedores', nuevo); }
  function save() {
    if (!form.proveedor) return alert('Introduce el proveedor.');
    if (form.id) replaceItem('pedidos', form);
    else { const n=config.sigPedido; nextNum('sigPedido','PED-'); addItem('pedidos', {...form, id:n, num:`PED-${n}`}); }
    setModal(null);
  }
  const statBadge = e => { const m={'Pendiente':'amber','En tránsito':'blue','Recibido':'green','Cancelado':'red'}; return <Badge variant={m[e]||'gray'}>{e}</Badge>; };

  return (
    <div>
      <SearchBar value={q} onChange={setQ} placeholder="Buscar pedido, proveedor...">
        <Btn variant="primary" onClick={newPed} title="Crear nuevo pedido a proveedor">+ Nuevo pedido</Btn>
      </SearchBar>
      <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:theme.surface2 }}>
              {[['Nº Pedido','num'],['Fecha','fecha'],['Proveedor','proveedor'],['Artículos',null],['Total',null],['Estado','estado'],[''  ,null]].map(([label,field]) => (
                <SortableTh key={label+field} label={label} field={field} sortField={sfP} sortDir={sdP} onSort={hsP} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedPedidos.map((p, i) => {
              const { total } = calcLineas(p.lineas);
              return (
                <tr key={p.id} style={{ borderBottom:`1px solid ${theme.border}`, background: i%2 ? theme.surface2 : 'transparent' }}>
                  <td style={{ padding:'10px 14px', fontWeight:600, color:'#1a56db' }}>{p.num}</td>
                  <td style={{ padding:'10px 14px' }}>{new Date(p.fecha).toLocaleDateString('es-ES')}</td>
                  <td style={{ padding:'10px 14px', fontWeight:500 }}>{p.proveedor}</td>
                  <td style={{ padding:'10px 14px', color:theme.muted, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.lineas.map(l=>l.desc).filter(Boolean).join(', ')}</td>
                  <td style={{ padding:'10px 14px', fontWeight:500 }}>{formatEur(total)}</td>
                  <td style={{ padding:'10px 14px' }}>{statBadge(p.estado)}</td>
                  <td style={{ padding:'10px 14px' }}>
                    <div style={{ display:'flex', gap:4 }}>
                      <Btn size="sm" variant="ghost" title="Editar" onClick={() => { setForm({...p, lineas:p.lineas.map(l=>({...l}))}); setModal('form'); }}>✏️</Btn>
                      <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} title="Eliminar" onClick={() => setConfirm(p.id)}>🗑️</Btn>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && <tr><td colSpan={8} style={{ padding:30, textAlign:'center', color:theme.faint }}>No hay pedidos</td></tr>}
          </tbody>
        </table>
      </div>
      {modal === 'form' && form && (
        <Modal title={form.id ? `Editar ${form.num}` : 'Nuevo pedido'} onClose={() => setModal(null)} width={680}>
          <div style={{ marginBottom:12 }}>
            <ProveedorSelector
              proveedores={proveedores}
              proveedorId={form.proveedorId}
              proveedorNombre={form.proveedor}
              onChange={handleProveedorChange}
              onNuevoProveedor={handleNuevoProveedor}
            />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
            <Input label="Fecha" type="date" value={form.fecha} onChange={v=>setForm(f=>({...f,fecha:v}))} />
            <Select label="Estado" value={form.estado} onChange={v=>setForm(f=>({...f,estado:v}))} options={PAGOS_PED} />
          </div>
          <div style={{ gridTemplateColumns: '1fr 1fr', display:'grid', gap: 10, marginBottom:12 }}>
            <Textarea label="Notas" value={form.notas} onChange={v=>setForm(f=>({...f,notas:v}))} rows={2} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color:theme.body, marginBottom: 6 }}>Líneas del pedido</div>
          <div style={{ background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:10, padding:10, marginBottom:12 }}>
            <LineasEditorLocal lineas={form.lineas} onChange={l=>setForm(f=>({...f,lineas:l}))} articulos={articulos} />
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={() => setModal(null)} title="Descartar cambios">Cancelar</Btn>
            <Btn variant="primary" onClick={save} title="Guardar">Guardar</Btn>
          </div>
        </Modal>
      )}
      {confirm && <Confirm msg="¿Eliminar este pedido?" onOk={() => { deleteItem('pedidos', confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

function LineasEditorLocal({ lineas, onChange, articulos }) {
  const theme = useT();
  const add = () => onChange([...lineas, { desc:'', cant:1, precio:0, iva:21 }]);
  const upd = (i, f, v) => {
    const next = lineas.map((l, idx) => {
      if (idx !== i) return l;
      if (f === 'ivaIncl') return { ...l, ivaIncl: v };
      if (f === 'iva') {
        const newIva = parseFloat(v) || 0;
        if (l.ivaIncl) {
          const bruto = l.precio * (1 + (l.iva||0)/100);
          return { ...l, iva: newIva, precio: bruto / (1 + newIva/100) };
        }
        return { ...l, iva: newIva };
      }
      if (f === 'precio') {
        const num = parseFloat(v) || 0;
        return { ...l, precio: l.ivaIncl ? num / (1 + (l.iva||0)/100) : num };
      }
      return { ...l, [f]: f==='cant' ? parseFloat(v)||0 : v };
    });
    onChange(next);
  };
  const precioMostrado = (l) => l.ivaIncl ? Math.round(l.precio * (1 + (l.iva||0)/100) * 100) / 100 : l.precio;
  const del = (i) => onChange(lineas.filter((_, idx) => idx !== i));
  return (
    <div>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
        <thead><tr style={{ background:theme.surface2 }}>{['Artículo/Descripción','Cant.','Precio u.','IVA incl.','IVA%','Total',''].map(h=>(<th key={h} style={{ padding:'6px 8px', textAlign:'left', color:theme.muted, fontSize:11, fontWeight:600, borderBottom:`1px solid ${theme.border}` }}>{h}</th>))}</tr></thead>
        <tbody>
          {lineas.map((l,i)=>(
            <tr key={i}>
              <td style={{ padding:'4px 4px' }}><input list={`arts-p-${i}`} value={l.desc} onChange={e=>upd(i,'desc',e.target.value)} style={{ width:'100%', padding:'5px 8px', border:`1px solid ${theme.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }} /><datalist id={`arts-p-${i}`}>{articulos?.map(a=><option key={a.id} value={a.nombre}/>)}</datalist></td>
              <td style={{ padding:'4px 4px' }}><input type="number" value={l.cant} onChange={e=>upd(i,'cant',e.target.value)} style={{ width:55, padding:'5px 8px', border:`1px solid ${theme.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }}/></td>
              <td style={{ padding:'4px 4px' }}><input type="number" value={precioMostrado(l)} onChange={e=>upd(i,'precio',e.target.value)} style={{ width:75, padding:'5px 8px', border:`1px solid ${theme.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }}/></td>
              <td style={{ padding:'4px 4px', textAlign:'center' }}><input type="checkbox" checked={!!l.ivaIncl} onChange={e=>upd(i,'ivaIncl',e.target.checked)} title="El precio introducido ya incluye el IVA" /></td>
              <td style={{ padding:'4px 4px' }}><select value={l.iva} onChange={e=>upd(i,'iva',e.target.value)} style={{ padding:'5px 6px', border:`1px solid ${theme.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }}>{[0,4,10,21].map(v=><option key={v} value={v}>{v}%</option>)}</select></td>
              <td style={{ padding:'4px 8px', fontWeight:600 }}>{formatEur(l.cant*l.precio)}</td>
              <td style={{ padding:'4px 4px' }}><button onClick={()=>del(i)} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:16 }}>✕</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={add} style={{ marginTop:8, background:'none', border:'1px dashed #d1d5db', borderRadius:8, padding:'5px 12px', fontSize:12, color:theme.muted, cursor:'pointer' }}>+ Añadir línea</button>
    </div>
  );
}

// ── RECORDATORIOS (notas + avisos programados de PACO) ──
function AvisosPaco() {
  const theme = useT();
  const [avisos, setAvisos] = useState(null);
  const cargar = useCallback(() => {
    fetch('/api/recordatorios', { headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}` } })
      .then(r => r.json()).then(d => setAvisos(Array.isArray(d) ? d : [])).catch(() => setAvisos([]));
  }, []);
  useEffect(() => { cargar(); const t = setInterval(cargar, 30000); return () => clearInterval(t); }, [cargar]);
  async function borrar(id) {
    await fetch(`/api/recordatorios/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}` } });
    cargar();
  }
  const fmt = iso => new Date(iso).toLocaleString('es-ES', { weekday:'short', day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' });
  if (avisos === null) return null;
  return (
    <div style={{ background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:12, padding:'14px 16px', marginBottom:16 }}>
      <div style={{ fontSize:13, fontWeight:700, color:theme.ink, marginBottom:4 }}>⏰ Avisos de PACO</div>
      <div style={{ fontSize:11, color:theme.faint, marginBottom:10 }}>Se crean por WhatsApp escribiendo «paco recuérdame mañana que…» y PACO avisa al WhatsApp de la tienda a su hora.</div>
      {!avisos.length && <div style={{ fontSize:12, color:theme.faint }}>No hay avisos programados.</div>}
      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {avisos.map(a => (
          <div key={a.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, background:theme.surface2, opacity: a.enviado ? 0.6 : 1 }}>
            <span style={{ fontSize:11, fontWeight:600, color:theme.muted, minWidth:130 }}>{fmt(a.cuando)}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, color:theme.ink }}>{a.texto}</div>
              {(a.pedido_por || a.origen) && <div style={{ fontSize:11, color:theme.faint }}>{[a.pedido_por && `pedido por ${a.pedido_por}`, a.origen].filter(Boolean).join(' · ')}</div>}
            </div>
            <span style={{ fontSize:11, color: a.enviado ? theme.faint : '#0e9f6e' }}>{a.enviado ? '✓ enviado' : 'pendiente'}</span>
            <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} title="Eliminar aviso" onClick={() => borrar(a.id)}>🗑️</Btn>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── APUNTES (ahora «Recordatorios» en la interfaz) ─────
export function Apuntes() {
  const theme = useT();
  const { data, addItem, replaceItem, deleteItem } = useStore();
  const { apuntes } = data;
  const { q, setQ, filtered } = useSearch(apuntes, ['titulo','texto'], 'apuntes');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [quick, setQuick] = useState({ titulo:'', texto:'' });

  function saveQuick() {
    if (!quick.titulo.trim()) return;
    addItem('apuntes', { ...quick, id: Date.now(), fecha: today() });
    setQuick({ titulo:'', texto:'' });
  }

  function save() {
    if (!form.titulo) return alert('El título es obligatorio.');
    if (form.id) replaceItem('apuntes', form);
    else addItem('apuntes', { ...form, id: Date.now(), fecha: today() });
    setModal(null);
  }

  return (
    <div>
      <AvisosPaco />
      {/* Entrada rápida */}
      <div style={{ background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:12, padding:'14px 16px', marginBottom:16 }}>
        <input
          value={quick.titulo}
          onChange={e => setQuick(q => ({ ...q, titulo: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveQuick(); } }}
          placeholder="Título del recordatorio..."
          style={{ width:'100%', border:'none', outline:'none', fontSize:14, fontWeight:600, color:theme.ink, marginBottom:6, fontFamily:'inherit', boxSizing:'border-box' }}
        />
        <textarea
          value={quick.texto}
          onChange={e => setQuick(q => ({ ...q, texto: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) saveQuick(); }}
          placeholder="Escribe el contenido... (Ctrl+Enter para guardar)"
          rows={3}
          style={{ width:'100%', border:'none', outline:'none', fontSize:13, color:theme.body, resize:'vertical', fontFamily:'inherit', lineHeight:1.6, boxSizing:'border-box' }}
        />
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:6 }}>
          <Btn variant="primary" onClick={saveQuick} disabled={!quick.titulo.trim()} title="Guardar recordatorio rápido">+ Guardar recordatorio</Btn>
        </div>
      </div>

      <SearchBar value={q} onChange={setQ} placeholder="Buscar recordatorios..." />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:12 }}>
        {filtered.map(a => (
          <div key={a.id} style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, padding:'14px 16px', position:'relative' }}>
            <div style={{ fontSize:13, fontWeight:600, color:theme.ink, marginBottom:6 }}>{a.titulo}</div>
            <div style={{ fontSize:12, color:theme.muted, lineHeight:1.6, marginBottom:10 }}>{a.texto}</div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:11, color:theme.faint }}>{new Date(a.fecha).toLocaleDateString('es-ES')}</span>
              <div style={{ display:'flex', gap:4 }}>
                <Btn size="sm" variant="ghost" title="Editar" onClick={() => { setForm({...a}); setModal('form'); }}>✏️</Btn>
                <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} title="Eliminar" onClick={() => setConfirm(a.id)}>🗑️</Btn>
              </div>
            </div>
          </div>
        ))}
        {!filtered.length && <div style={{ padding:30, textAlign:'center', color:theme.faint, gridColumn:'1/-1' }}>No hay recordatorios</div>}
      </div>
      {modal === 'form' && form && (
        <Modal title={form.id ? 'Editar recordatorio' : 'Nuevo recordatorio'} onClose={() => setModal(null)} width={460}>
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
            <Input label="Título" value={form.titulo} onChange={v=>setForm(f=>({...f,titulo:v}))} />
            <Textarea label="Contenido" value={form.texto} onChange={v=>setForm(f=>({...f,texto:v}))} rows={4} />
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={() => setModal(null)} title="Descartar cambios">Cancelar</Btn>
            <Btn variant="primary" onClick={save} title="Guardar">Guardar</Btn>
          </div>
        </Modal>
      )}
      {confirm && <Confirm msg="¿Eliminar este recordatorio?" onOk={() => { deleteItem('apuntes', confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ── PROVEEDORES ─────────────────────────────────────────
export function Proveedores() {
  const theme = useT();
  const { data, addItem, replaceItem, deleteItem } = useStore();
  const { proveedores = [] } = data;
  const { q, setQ, filtered } = useSearch(proveedores, ['nombre','nombreComercial','nombreEmpresa','cif','telefono','email','contacto'], 'proveedores');
  const { sorted: sortedProv, sortField: sfPV, sortDir: sdPV, handleSort: hsPV } = useSortable(filtered);
  const [modal, setModal] = useState(null);
  const [form, setForm]   = useState(null);
  const [confirm, setConfirm] = useState(null);

  // nombre sigue siendo el campo principal para compatibilidad con facturas/importador
  const newProv = () => ({ id:null, nombre:'', nombreComercial:'', nombreEmpresa:'', cif:'', telefono:'', email:'', direccion:'', web:'', contacto:'', notas:'' });
  const upd = (f,v) => setForm(x => ({ ...x, [f]:v }));

  function save() {
    const nombreComercial = form.nombreComercial || form.nombre || '';
    if (!nombreComercial) return alert('El nombre comercial es obligatorio.');
    // nombre = nombreComercial para compatibilidad con el resto de la app
    const toSave = { ...form, nombreComercial, nombre: nombreComercial };
    if (form.id) replaceItem('proveedores', toSave);
    else addItem('proveedores', { ...toSave, id: Date.now() });
    setModal(null);
  }

  // Al abrir edición, rellenar nombreComercial desde nombre si aún no existe
  function openEdit(p) {
    setForm({ nombreComercial: p.nombre || '', nombreEmpresa: '', ...p });
    setModal('form');
  }

  const displayNombre = p => p.nombreComercial || p.nombre || '—';

  return (
    <div>
      <SearchBar value={q} onChange={setQ} placeholder="Buscar proveedor, CIF, contacto...">
        <Btn variant="ghost" title="Exportar proveedores a Excel" onClick={() => exportProveedores(sortedProv)}>📊 Exportar Excel</Btn>
        <Btn variant="primary" title="Crear nuevo proveedor" onClick={() => { setForm(newProv()); setModal('form'); }}>+ Nuevo proveedor</Btn>
      </SearchBar>
      <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:theme.surface2 }}>
              {[['Nombre comercial','nombreComercial'],['Empresa','nombreEmpresa'],['CIF','cif'],['Teléfono','telefono'],['Email','email'],['Contacto','contacto'],[''  ,null]].map(([label,field]) => (
                <SortableTh key={label+field} label={label} field={field} sortField={sfPV} sortDir={sdPV} onSort={hsPV} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedProv.map((p, i) => (
              <tr key={p.id} style={{ borderBottom:`1px solid ${theme.border}`, background: i%2 ? theme.surface2 : 'transparent' }}>
                <td style={{ padding:'10px 14px', fontWeight:600 }}>{displayNombre(p)}</td>
                <td style={{ padding:'10px 14px', color:theme.muted }}>{p.nombreEmpresa||'—'}</td>
                <td style={{ padding:'10px 14px', color:theme.muted }}>{p.cif||'—'}</td>
                <td style={{ padding:'10px 14px' }}>{p.telefono||'—'}</td>
                <td style={{ padding:'10px 14px', color:theme.muted }}>{p.email||'—'}</td>
                <td style={{ padding:'10px 14px' }}>{p.contacto||'—'}</td>
                <td style={{ padding:'10px 14px' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <Btn size="sm" variant="ghost" title="Editar" onClick={() => openEdit(p)}>✏️</Btn>
                    <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} title="Eliminar" onClick={() => setConfirm(p.id)}>🗑️</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={8} style={{ padding:30, textAlign:'center', color:theme.faint }}>No hay proveedores</td></tr>}
          </tbody>
        </table>
      </div>

      {modal === 'form' && form && (
        <Modal title={form.id ? 'Editar proveedor' : 'Nuevo proveedor'} onClose={() => setModal(null)} width={560}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
            <Input label="Nombre comercial *" value={form.nombreComercial} onChange={v=>upd('nombreComercial',v)} />
            <Input label="Nombre de empresa"  value={form.nombreEmpresa||''} onChange={v=>upd('nombreEmpresa',v)} />
            <Input label="CIF / NIF"  value={form.cif}      onChange={v=>upd('cif',v)} />
            <Input label="Teléfono"   value={form.telefono}  onChange={v=>upd('telefono',v)} />
            <Input label="Email"      value={form.email}     onChange={v=>upd('email',v)} type="email" />
            <Input label="Web"        value={form.web}       onChange={v=>upd('web',v)} />
            <Input label="Persona de contacto" value={form.contacto} onChange={v=>upd('contacto',v)} />
            <div style={{ gridColumn:'1/-1' }}><Input label="Dirección" value={form.direccion} onChange={v=>upd('direccion',v)} /></div>
            <div style={{ gridColumn:'1/-1' }}><Textarea label="Notas" value={form.notas||''} onChange={v=>upd('notas',v)} rows={2} /></div>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={() => setModal(null)} title="Descartar cambios">Cancelar</Btn>
            <Btn variant="primary" onClick={save} title="Guardar">Guardar</Btn>
          </div>
        </Modal>
      )}
      {confirm && <Confirm msg="¿Eliminar este proveedor?" onOk={() => { deleteItem('proveedores', confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ── FACTURAS DE PROVEEDOR ────────────────────────────────
const ESTADOS_FPROV = ['Pendiente','Pagada','Vencida','Anulada'];

export function FacturasProveedor() {
  const theme = useT();
  const { data, addItem, replaceItem, deleteItem } = useStore();
  const { facturasProveedor = [], proveedores = [], articulos = [] } = data;
  const facturasConLineas = facturasProveedor.map(f => ({ ...f, _lineasTexto: (f.lineas || []).map(l => l.desc || '').join(' ') }));
  const { q, setQ, filtered } = useSearch(facturasConLineas, ['proveedorNombre','proveedor','numFactura','estado','concepto','notas','_lineasTexto'], 'facturasProveedor');
  const { sorted: sortedFP, sortField: sfFP, sortDir: sdFP, handleSort: hsFP } = useSortable(filtered, 'num', 'desc');
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [filtroEstados, setFiltroEstados] = useLocalStorage('filtroEstados_facturasProveedor', []);
  const [exportModal, setExportModal] = useState(false);
  const [exportDesde, setExportDesde] = useState('');
  const [exportHasta, setExportHasta] = useState('');
  const [revisarForm, setRevisarForm] = useState(null);

  function revisarLinea(linea) {
    const built = construirFormArticuloDesdeLinea(linea, articulos);
    if (built.id) {
      // Artículo ya existente: el precio de compra se actualiza al momento (el stock no, requiere "Añadir stock")
      const { _cantidadSugerida, ...articuloActualizado } = built;
      replaceItem('articulos', articuloActualizado);
    }
    setRevisarForm(built);
  }
  function guardarRevisado(finalForm) {
    if (finalForm.id) replaceItem('articulos', finalForm);
    else addItem('articulos', { ...finalForm, id: Date.now(), precioCompra:+finalForm.precioCompra||0, precio:+finalForm.precio||0, iva:+finalForm.iva||21, stock:+finalForm.stock||0, stockMin:+finalForm.stockMin||0 });
    setRevisarForm(null);
  }

  const pendientes = facturasProveedor.filter(f => f.estado === 'Pendiente').length;
  const totalPend  = facturasProveedor.filter(f => f.estado === 'Pendiente').reduce((s,f) => s + (parseFloat(f.total||f.importe)||0), 0);

  const newFact = () => ({ id:null, fecha: today(), proveedorId:'', proveedorNombre:'', numFactura:'', total:0, estado:'Pendiente' });
  const upd = (f,v) => setForm(x => ({ ...x, [f]:v }));

  function setProveedor(id) {
    const p = proveedores.find(x => String(x.id) === String(id));
    setForm(f => ({ ...f, proveedorId: id, proveedorNombre: p ? p.nombre : '' }));
  }

  function save() {
    if (!form.proveedorNombre) return alert('Selecciona o introduce un proveedor.');
    if (!form.numFactura)      return alert('El número de factura es obligatorio.');
    if (form.id) replaceItem('facturasProveedor', form);
    else addItem('facturasProveedor', { ...form, id: Date.now() });
    setModal(null);
  }

  const shown = filtroEstados.length ? sortedFP.filter(f => filtroEstados.includes(f.estado)) : sortedFP;

  function estadoBadge(e) {
    const m = { Pagada:'green', Pendiente:'amber', Vencida:'red', Anulada:'gray' };
    return <Badge variant={m[e]||'gray'}>{e}</Badge>;
  }

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
        <StatCard label="Total facturas"  value={facturasProveedor.length} />
        <StatCard label="Pendientes"      value={pendientes} color="#c27803" />
        <StatCard label="Importe pendiente" value={formatEur(totalPend)} color="#ef4444" />
      </div>
      <SearchBar value={q} onChange={setQ} placeholder="Buscar proveedor, nº factura, estado...">
        <MultiSelect options={ESTADOS_FPROV} selected={filtroEstados} onChange={setFiltroEstados} placeholder="Estado: Todos" />
        <Btn variant="ghost" title="Exportar a Excel" onClick={() => setExportModal(true)}>📊 Exportar Excel</Btn>
        <Btn variant="primary" title="Crear nueva factura de proveedor" onClick={() => { setForm(newFact()); setModal('form'); }}>+ Nueva factura</Btn>
      </SearchBar>
      <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:theme.surface2 }}>
              {[['Fecha','fecha'],['Proveedor','proveedorNombre'],['Nº Factura','numFactura'],['Total','total'],['Estado','estado'],[''  ,null]].map(([label,field]) => (
                <SortableTh key={label+field} label={label} field={field} sortField={sfFP} sortDir={sdFP} onSort={hsFP} />
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((f, i) => (
              <tr key={f.id} style={{ borderBottom:`1px solid ${theme.border}`, background: i%2 ? theme.surface2 : 'transparent' }}>
                <td style={{ padding:'10px 14px', color:theme.muted, whiteSpace:'nowrap' }}>{new Date(f.fecha).toLocaleDateString('es-ES')}</td>
                <td style={{ padding:'10px 14px', fontWeight:500 }}>{f.proveedorNombre || f.proveedor}</td>
                <td style={{ padding:'10px 14px', fontWeight:600, color:'#1a56db' }}>{f.numFactura}</td>
                <td style={{ padding:'10px 14px', fontWeight:600 }}>{formatEur(parseFloat(f.total||f.importe)||0)}</td>
                <td style={{ padding:'10px 14px', position:'relative' }}>
                  <EstadoQuick estado={f.estado} estados={ESTADOS_FPROV}
                    colores={{ Pendiente:'#fef3c7', Pagada:'#dcfce7', Vencida:'#fee2e2', Anulada:'#f3f4f6' }}
                    textos={{ Pendiente:'#92400e', Pagada:'#166534', Vencida:'#991b1b', Anulada:'#374151' }}
                    onChange={v => replaceItem('facturasProveedor', { ...f, estado: v })} />
                </td>
                <td style={{ padding:'10px 14px' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <Btn size="sm" variant="ghost" title="Editar" onClick={() => { setForm({...f}); setModal('form'); }}>✏️</Btn>
                    <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} title="Eliminar" onClick={() => setConfirm(f.id)}>🗑️</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={6} style={{ padding:30, textAlign:'center', color:theme.faint }}>No hay facturas de proveedor</td></tr>}
          </tbody>
        </table>
      </div>

      {modal === 'form' && form && (
        <Modal title={form.id ? 'Editar factura proveedor' : 'Nueva factura proveedor'} onClose={() => setModal(null)} width={480}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
            <Input label="Fecha" type="date" value={form.fecha} onChange={v=>upd('fecha',v)} />
            <Input label="Nº Factura proveedor *" value={form.numFactura} onChange={v=>upd('numFactura',v)} placeholder="FAC-2026-001" />
            <div style={{ gridColumn:'1/-1', display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>Proveedor</label>
              <select value={form.proveedorId||''} onChange={e=>setProveedor(e.target.value)}
                style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }}>
                <option value="">— Seleccionar proveedor —</option>
                {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
              {!form.proveedorId && <Input label="" value={form.proveedorNombre} onChange={v=>setForm(f=>({...f,proveedorNombre:v}))} placeholder="O escribe el nombre directamente" />}
            </div>
            <Input label="Total (€) *" type="number" step="0.01" value={form.total} onChange={v=>upd('total',v)} />
            <Select label="Estado" value={form.estado} onChange={v=>upd('estado',v)} options={ESTADOS_FPROV} />
          </div>
          {(form.lineas||[]).length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:6 }}>Líneas de factura</div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, border:`1px solid ${theme.border}`, borderRadius:8, overflowX:'auto' }}>
                <thead>
                  <tr style={{ background:theme.surface2 }}>
                    <th style={{ padding:'6px 10px', textAlign:'left', fontWeight:600, color:theme.body }}>Descripción</th>
                    <th style={{ padding:'6px 10px', textAlign:'right', fontWeight:600, color:theme.body }}>Cant.</th>
                    <th style={{ padding:'6px 10px', textAlign:'right', fontWeight:600, color:theme.body }}>Precio</th>
                    <th style={{ padding:'6px 10px', textAlign:'right', fontWeight:600, color:theme.body }}>IVA%</th>
                    <th style={{ padding:'6px 10px', textAlign:'right', fontWeight:600, color:theme.body }}>Total</th>
                    <th style={{ padding:'6px 10px', color:theme.body }}></th>
                  </tr>
                </thead>
                <tbody>
                  {form.lineas.map((l, i) => (
                    <tr key={i} style={{ borderTop:`1px solid ${theme.border}` }}>
                      <td style={{ padding:'6px 10px' }}>{l.ref ? <span style={{ fontFamily:'monospace', fontWeight:600, marginRight:6 }}>{l.ref}</span> : null}{l.desc}</td>
                      <td style={{ padding:'6px 10px', textAlign:'right' }}>{l.cant??1}</td>
                      <td style={{ padding:'6px 10px', textAlign:'right' }}>{formatEur(parseFloat(l.precio)||0)}</td>
                      <td style={{ padding:'6px 10px', textAlign:'right' }}>{l.iva??21}%</td>
                      <td style={{ padding:'6px 10px', textAlign:'right', fontWeight:600 }}>{formatEur((l.cant??1)*(parseFloat(l.precio)||0))}</td>
                      <td style={{ padding:'6px 10px' }}>
                        <Btn size="sm" variant="ghost" title="Revisar artículo (crear o actualizar en el catálogo)" onClick={() => revisarLinea(l)}>🔍</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={() => setModal(null)} title="Descartar cambios">Cancelar</Btn>
            <Btn variant="primary" onClick={save} title="Guardar">Guardar</Btn>
          </div>
        </Modal>
      )}
      {confirm && <Confirm msg="¿Eliminar esta factura?" onOk={() => { deleteItem('facturasProveedor', confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />}
      {revisarForm && (
        <ArticuloFormModal form={revisarForm} setForm={setRevisarForm} onClose={() => setRevisarForm(null)} onSave={guardarRevisado} />
      )}

      {exportModal && (
        <Modal title="Exportar facturas a Excel" onClose={() => setExportModal(false)} width={360}>
          <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:16 }}>
            <Input label="Desde (fecha)" value={exportDesde} onChange={setExportDesde} type="date" />
            <Input label="Hasta (fecha)" value={exportHasta} onChange={setExportHasta} type="date" />
            <div style={{ fontSize:12, color:theme.muted }}>
              {(() => {
                const filtradas = shown.filter(f => {
                  if (exportDesde && f.fecha < exportDesde) return false;
                  if (exportHasta && f.fecha > exportHasta) return false;
                  return true;
                });
                return `${filtradas.length} factura${filtradas.length !== 1 ? 's' : ''} ${exportDesde || exportHasta ? 'en el rango seleccionado' : '(todas las visibles)'}`;
              })()}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={() => setExportModal(false)}>Cancelar</Btn>
            <Btn variant="primary" onClick={() => {
              const filtradas = shown.filter(f => {
                if (exportDesde && f.fecha < exportDesde) return false;
                if (exportHasta && f.fecha > exportHasta) return false;
                return true;
              });
              if (!filtradas.length) { alert('No hay facturas en ese rango.'); return; }
              exportFacturasProveedor(filtradas, exportDesde, exportHasta);
              setExportModal(false);
            }}>📊 Exportar</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── IMPORTADOR FACTURAS POP3 ────────────────────────────
export function ImportadorFacturas() {
  const theme = useT();
  const { addItem, replaceItem, data } = useStore();
  const proveedores = data?.proveedores || [];
  const articulos = data?.articulos || [];
  const importados = new Set((data?.facturasProveedor || []).map(f => f.emailUid).filter(Boolean));
  const [lista, setLista]       = useState(null);
  const [cargando, setCargando] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [textosPdf, setTextosPdf] = useState(null);
  const [pdfUrl, setPdfUrl]     = useState(null);
  const [form, setForm] = useState(null);
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState('');
  const [revisarForm, setRevisarForm] = useState(null);

  function revisarLinea(linea) {
    const built = construirFormArticuloDesdeLinea(linea, articulos);
    if (built.id) {
      // Artículo ya existente: el precio de compra se actualiza al momento (el stock no, requiere "Añadir stock")
      const { _cantidadSugerida, ...articuloActualizado } = built;
      replaceItem('articulos', articuloActualizado);
    }
    setRevisarForm(built);
  }
  function guardarRevisado(finalForm) {
    if (finalForm.id) replaceItem('articulos', finalForm);
    else addItem('articulos', { ...finalForm, id: Date.now(), precioCompra:+finalForm.precioCompra||0, precio:+finalForm.precio||0, iva:+finalForm.iva||21, stock:+finalForm.stock||0, stockMin:+finalForm.stockMin||0 });
    setRevisarForm(null);
  }

  const token = () => localStorage.getItem('ri_token');

  const cargarLista = useCallback(async () => {
    setCargando(true); setError(''); setLista(null); setSeleccionado(null); setForm(null); setTextosPdf(null); setPdfUrl(null);
    try {
      const r = await fetch('/api/pop3/lista', { headers:{ Authorization:'Bearer '+token() } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error al conectar');
      setLista(d);
    } catch(e) { setError(e.message); }
    setCargando(false);
  }, []);

  const ignorarRemitente = useCallback(async (msg) => {
    if (!window.confirm(`¿Ignorar todos los emails de "${msg.emailRemitente}"? No volverán a aparecer.`)) return;
    await fetch('/api/pop3/ignorados', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() },
      body: JSON.stringify({ email: msg.emailRemitente }),
    });
    setLista(l => l.filter(m => m.emailRemitente !== msg.emailRemitente));
    if (seleccionado?.emailRemitente === msg.emailRemitente) { setSeleccionado(null); setForm(null); setTextosPdf(null); }
  }, [seleccionado]);

  const abrirMensaje = useCallback(async (msg) => {
    setSeleccionado(msg); setTextosPdf(null); setPdfUrl(null); setForm(null); setGuardado(false); setError('');
    try {
      const r = await fetch(`/api/pop3/mensaje/${msg.num}`, { headers:{ Authorization:'Bearer '+token() } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setTextosPdf(d.textos);
      if (d.pdfUrl) setPdfUrl(d.pdfUrl);
      // Si Claude devolvió datos estructurados, usarlos directamente
      const claudeDatos = d.textos.find(t => t.datos)?.datos;
      if (claudeDatos) {
        const prov = claudeDatos.proveedor || {};
        const nombreDe = msg.de.replace(/<.*>/, '').replace(/"/g,'').trim();
        const emailDe = (msg.de.match(/<([^>]+)>/) || [])[1] || msg.de.trim();
        setForm(enrichProveedor({
          proveedor:          prov.nombre || nombreDe,
          proveedorEmpresa:   '',
          proveedorNif:       prov.nif || '',
          proveedorDireccion: prov.direccion || '',
          proveedorTel:       prov.telefono || '',
          proveedorEmail:     prov.email || emailDe,
          numFactura: claudeDatos.num || '',
          fecha:      claudeDatos.fecha || d.fecha || today(),
          importe:    claudeDatos.total != null ? String(claudeDatos.total) : '',
          concepto:   msg.asunto,
          notas:      '',
          lineas:     claudeDatos.lineas || [],
        }));
      } else {
        const texto = d.textos.map(t => t.texto).join('\n');
        const importeMatch = texto.match(/total[^\d]*(\d+[.,]\d{2})/i);
        const fechaMatch   = texto.match(/(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})/);
        const numMatch     = texto.match(/(?:factura|fra\.?|nº|n°)[^\w]*([A-Z0-9\-\/]+)/i);
        let fechaStr = d.fecha || today();
        if (fechaMatch) {
          const [, dd, mm, yy] = fechaMatch;
          const yyyy = yy.length === 2 ? '20' + yy : yy;
          fechaStr = `${yyyy}-${mm.padStart(2,'0')}-${dd.padStart(2,'0')}`;
        }
        const nombre = msg.de.replace(/<.*>/, '').replace(/"/g,'').trim();
        const emailDe2 = (msg.de.match(/<([^>]+)>/) || [])[1] || msg.de.trim();
        setForm({
          proveedor:          nombre || emailDe2,
          proveedorNif:       '',
          proveedorDireccion: '',
          proveedorTel:       '',
          proveedorEmail:     emailDe2,
          numFactura: numMatch ? numMatch[1] : '',
          fecha:      fechaStr,
          importe:    importeMatch ? importeMatch[1].replace(',','.') : '',
          concepto:   msg.asunto,
          notas:      '',
          lineas:     [],
        });
      }
    } catch(e) { setError(e.message); setTextosPdf([]); }
  }, []);

  function guardar() {
    if (!form.proveedor || !form.fecha || !form.importe) { setError('Proveedor, fecha e importe son obligatorios.'); return; }

    // Buscar proveedor existente por NIF o nombre
    let provId = null;
    const nifLimpio = (form.proveedorNif || '').replace(/\s/g,'').toUpperCase();
    const nombreLower = form.proveedor.toLowerCase().trim();
    let existente = nifLimpio
      ? proveedores.find(p => p.cif && p.cif.replace(/\s/g,'').toUpperCase() === nifLimpio)
      : null;
    if (!existente)
      existente = proveedores.find(p => (p.nombreComercial || p.nombre || '').toLowerCase().trim() === nombreLower);

    if (existente) {
      provId = existente.id;
      const actualizado = { ...existente };
      if (!existente.cif       && form.proveedorNif)       actualizado.cif       = form.proveedorNif;
      if (!existente.direccion && form.proveedorDireccion) actualizado.direccion = form.proveedorDireccion;
      if (!existente.telefono  && form.proveedorTel)       actualizado.telefono  = form.proveedorTel;
      if (!existente.email     && form.proveedorEmail)     actualizado.email     = form.proveedorEmail;
      if (!existente.nombreEmpresa && form.proveedorEmpresa) actualizado.nombreEmpresa = form.proveedorEmpresa;
      if (JSON.stringify(actualizado) !== JSON.stringify(existente)) replaceItem('proveedores', actualizado);
    } else {
      const nuevo = { id: Date.now(), nombre: form.proveedor, nombreComercial: form.proveedor, nombreEmpresa: form.proveedorEmpresa || '', cif: form.proveedorNif || '', telefono: form.proveedorTel || '', email: form.proveedorEmail || '', direccion: form.proveedorDireccion || '', web: '', contacto: '', notas: '' };
      addItem('proveedores', nuevo);
      provId = nuevo.id;
    }

    addItem('facturasProveedor', {
      id:              Date.now() + 1,
      proveedorId:     provId,
      proveedorNombre: form.proveedor,
      numFactura:      form.numFactura,
      fecha:           form.fecha,
      total:           parseFloat(form.importe) || 0,
      estado:          'Pagada',
      concepto:        form.concepto,
      notas:           form.notas,
      lineas:          form.lineas || [],
      emailUid:        seleccionado?.num ?? null,
    });
    setGuardado(true); setForm(null); setTextosPdf(null); setSeleccionado(null);
  }

  const upd = (f, v) => setForm(x => ({ ...x, [f]: v }));

  // Devuelve campos del proveedor existente si coincide por nombre/NIF
  function enrichProveedor(campos) {
    const nombreLower = (campos.proveedor || '').toLowerCase().trim();
    const nifLimpio   = (campos.proveedorNif || '').replace(/\s/g,'').toUpperCase();
    const existente   = proveedores.find(p =>
      (nifLimpio && p.cif && p.cif.replace(/\s/g,'').toUpperCase() === nifLimpio) ||
      (p.nombreComercial || p.nombre || '').toLowerCase().trim() === nombreLower
    );
    if (!existente) return campos;
    return {
      ...campos,
      proveedor:          existente.nombreComercial || existente.nombre || campos.proveedor,
      proveedorEmpresa:   existente.nombreEmpresa   || campos.proveedorEmpresa || '',
      proveedorNif:       existente.cif             || campos.proveedorNif || '',
      proveedorDireccion: existente.direccion       || campos.proveedorDireccion || '',
      proveedorTel:       existente.telefono        || campos.proveedorTel || '',
      proveedorEmail:     existente.email           || campos.proveedorEmail || '',
      _proveedorEncontrado: true,
    };
  }

  // Al cambiar el nombre manualmente, rellenar desde proveedor existente
  function updProveedor(nombre) {
    setForm(x => enrichProveedor({ ...x, proveedor: nombre }));
  }

  async function subirPdf(file) {
    if (!file) return;
    setSeleccionado({ num: null, asunto: file.name, de: '', fecha: '' });
    setTextosPdf(null); setForm(null); setGuardado(false); setError('');
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const r = await fetch('/api/pop3/analizar-pdf', { method:'POST', headers:{ Authorization:'Bearer '+token() }, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setTextosPdf(d.textos);
      if (d.pdfUrl) setPdfUrl(d.pdfUrl);
      const claudeDatos = d.textos.find(t => t.datos)?.datos;
      if (claudeDatos) {
        const prov = claudeDatos.proveedor || {};
        setForm(enrichProveedor({ proveedor: prov.nombre || '', proveedorEmpresa: '', proveedorNif: prov.nif || '', proveedorDireccion: prov.direccion || '', proveedorTel: prov.telefono || '', proveedorEmail: prov.email || '', numFactura: claudeDatos.num || '', fecha: claudeDatos.fecha || today(), importe: claudeDatos.total != null ? String(claudeDatos.total) : '', concepto: file.name.replace(/\.pdf$/i,''), notas: '', lineas: claudeDatos.lineas || [] }));
      }
    } catch(e) { setError(e.message); setTextosPdf([]); }
  }

  return (
    <div style={{ display:'flex', gap:16, height:'calc(100vh - 140px)', minHeight:400 }}>

      {/* Lista de emails */}
      <div style={{ width:320, flexShrink:0, background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:12, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ padding:'12px 16px', borderBottom:`1px solid ${theme.border}` }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <div style={{ fontSize:13, fontWeight:700, color:theme.ink }}>📧 Emails con PDF adjunto</div>
            <Btn variant="primary" size="sm" onClick={cargarLista} disabled={cargando} title="Conectar al buzón y cargar emails">
              {cargando ? '⏳' : lista === null ? 'Conectar' : '↺ Actualizar'}
            </Btn>
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 10px', border:'1.5px dashed #d1d5db', borderRadius:8, cursor:'pointer', fontSize:12, color:theme.muted, background:theme.surface2 }}
            title="Subir un PDF manualmente para analizarlo con IA">
            <span>📄 Subir PDF manualmente</span>
            <input type="file" accept=".pdf,application/pdf" style={{ display:'none' }} onChange={e => { if(e.target.files[0]) subirPdf(e.target.files[0]); e.target.value=''; }} />
          </label>
          <ScannerBtn onPdf={subirPdf} />
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:8 }}>
          {lista === null && !cargando && (
            <div style={{ padding:24, textAlign:'center', color:theme.faint, fontSize:13 }}>
              Pulsa <b>Conectar</b> para buscar facturas en el correo de los últimos 50 mensajes.
            </div>
          )}
          {cargando && <div style={{ padding:20, textAlign:'center', color:theme.muted, fontSize:13 }}>⏳ Conectando al servidor de correo...</div>}
          {lista && lista.length === 0 && <div style={{ padding:20, textAlign:'center', color:theme.faint, fontSize:13 }}>No se encontraron emails con PDF adjunto.</div>}
          {lista && lista.map(msg => (
            <div key={msg.num} style={{ position:'relative', marginBottom:4 }}>
              <div onClick={() => abrirMensaje(msg)}
                style={{ padding:'10px 12px', paddingRight:32, borderRadius:8, cursor:'pointer',
                  background: seleccionado?.num === msg.num ? theme.primaryBg : importados.has(msg.num) ? (theme===THEME.dark ? 'rgba(16,185,129,0.12)' : '#f0fdf4') : theme.surface2,
                  border: seleccionado?.num === msg.num ? `1px solid ${theme.primary}` : importados.has(msg.num) ? `1px solid ${theme===THEME.dark ? 'rgba(16,185,129,0.35)' : '#bbf7d0'}` : '1px solid transparent' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  {importados.has(msg.num) && <Badge variant="green">✓</Badge>}
                  <div style={{ fontSize:12, fontWeight:600, color:theme.ink, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{msg.asunto}</div>
                </div>
                <div style={{ fontSize:11, color:theme.muted, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{msg.de.replace(/<.*>/, '').replace(/"/g,'').trim()}</div>
                <div style={{ fontSize:11, color:theme.faint, marginTop:2 }}>{msg.fecha}</div>
              </div>
              <button title="Ignorar este remitente" onClick={e => { e.stopPropagation(); ignorarRemitente(msg); }}
                style={{ position:'absolute', top:8, right:6, background:'none', border:'none', cursor:'pointer', fontSize:14, color:theme.faint, lineHeight:1, padding:2 }}>
                🚫
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Panel derecho */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:12, overflow:'hidden', minWidth:0 }}>
        {guardado && <div style={{ background:'#d1fae5', color:'#065f46', borderRadius:8, padding:'10px 16px', fontWeight:600, fontSize:13 }}>✓ Factura guardada correctamente en Facturas de proveedor</div>}
        {error && <div style={{ background:'#fee2e2', color:'#991b1b', borderRadius:8, padding:'10px 16px', fontSize:13 }}>⚠ {error}</div>}

        {!seleccionado ? (
          <div style={{ flex:1, background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', color:theme.faint, fontSize:14 }}>
            Selecciona un email de la lista para ver el contenido
          </div>
        ) : (
          <div style={{ flex:1, display:'flex', flexDirection:'column', gap:12, overflow:'hidden', minHeight:0 }}>

            {/* Fila superior: PDF | datos Claude */}
            <div style={{ flex:1, display:'flex', gap:12, overflow:'hidden', minHeight:0 }}>

              {/* Vista previa PDF */}
              {pdfUrl && (
                <div style={{ flex:'0 0 48%', display:'flex', flexDirection:'column', background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:12, overflow:'hidden' }}>
                  <div style={{ padding:'8px 14px', borderBottom:`1px solid ${theme.border}`, fontSize:12, fontWeight:700, color:theme.muted, textTransform:'uppercase', flexShrink:0 }}>📄 PDF original</div>
                  <iframe src={pdfUrl} title="Vista previa PDF" style={{ flex:1, border:'none', width:'100%' }} />
                </div>
              )}

              {/* Datos Claude */}
              <div style={{ flex:1, background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:12, padding:16, overflowY:'auto', minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:700, color:theme.muted, textTransform:'uppercase', marginBottom:8 }}>Contenido del PDF</div>
                {!textosPdf && <div style={{ color:theme.faint, fontSize:13 }}>⏳ Analizando PDF con IA...</div>}
                {textosPdf && textosPdf.map((t, i) => (
                  <div key={i} style={{ marginBottom:12 }}>
                    {textosPdf.length > 1 && <div style={{ fontSize:11, fontWeight:600, color:'#1a56db', marginBottom:4 }}>📎 {t.nombre}</div>}
                    {t.datos ? (
                      <div style={{ ...pastelBtn(theme,'green'), borderRadius:8, padding:10, fontSize:12 }}>
                        <div style={{ fontWeight:700, marginBottom:6 }}>✨ Analizado con IA</div>
                        <div><b>Nº Factura:</b> {t.datos.num || '—'}</div>
                        <div><b>Fecha:</b> {t.datos.fecha || '—'}</div>
                        <div><b>Cliente:</b> {t.datos.clienteNombre || '—'}</div>
                        <div><b>Total:</b> {t.datos.total ? t.datos.total.toFixed(2) + ' €' : '—'}</div>
                        {t.datos.lineas?.length > 0 && <div style={{ marginTop:6 }}><b>Líneas:</b> {t.datos.lineas.map(l => l.desc).join(', ')}</div>}
                      </div>
                    ) : (
                      <pre style={{ fontSize:11, color:theme.body, whiteSpace:'pre-wrap', lineHeight:1.6, margin:0, fontFamily:'monospace', background:theme.surface2, borderRadius:8, padding:10 }}>
                        {t.texto || '(no se pudo extraer texto — puede ser un PDF escaneado)'}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Formulario abajo */}
            {form && (
              <div style={{ background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:12, padding:16, flexShrink:0, overflowY:'auto', maxHeight:'45%' }}>
                <div style={{ fontSize:12, fontWeight:700, color:theme.muted, textTransform:'uppercase', marginBottom:10 }}>Datos de la factura — revisa y corrige si es necesario</div>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:theme.faint, textTransform:'uppercase' }}>Proveedor</div>
                  {form._proveedorEncontrado && <span style={{ fontSize:11, background:'#dbeafe', color:'#1e40af', borderRadius:4, padding:'1px 7px', fontWeight:600 }}>✓ Proveedor existente — revisa los datos</span>}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:10 }}>
                  <Input label="Nombre comercial" value={form.proveedor}           onChange={v=>updProveedor(v)} />
                  <Input label="Nombre empresa"   value={form.proveedorEmpresa||''} onChange={v=>upd('proveedorEmpresa',v)} />
                  <Input label="NIF / CIF"        value={form.proveedorNif||''}    onChange={v=>upd('proveedorNif',v)} />
                  <Input label="Teléfono"         value={form.proveedorTel||''}    onChange={v=>upd('proveedorTel',v)} />
                  <Input label="Dirección"        value={form.proveedorDireccion||''} onChange={v=>upd('proveedorDireccion',v)} />
                  <Input label="Email"            value={form.proveedorEmail||''} onChange={v=>upd('proveedorEmail',v)} />
                </div>
                <div style={{ fontSize:11, fontWeight:700, color:theme.faint, textTransform:'uppercase', marginBottom:6 }}>Factura</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 2fr', gap:10, marginBottom:10 }}>
                  <Input label="Nº factura"  value={form.numFactura} onChange={v=>upd('numFactura',v)} />
                  <Input label="Fecha"       value={form.fecha}      onChange={v=>upd('fecha',v)} type="date" />
                  <Input label="Importe (€)" value={form.importe}    onChange={v=>upd('importe',v)} type="number" />
                  <Input label="Concepto"    value={form.concepto}   onChange={v=>upd('concepto',v)} />
                </div>
                <Textarea label="Notas" value={form.notas} onChange={v=>upd('notas',v)} rows={2} />
                {form.lineas && form.lineas.length > 0 && (
                  <div style={{ marginTop:12 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:theme.muted, textTransform:'uppercase', marginBottom:6 }}>Líneas ({form.lineas.length})</div>
                    <div style={{ border:`1px solid ${theme.border}`, borderRadius:8, overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead><tr style={{ background:theme.surface2 }}>
                          {['Descripción','Cant','Precio unit.','IVA%','Total',''].map(h => (
                            <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:theme.muted, fontWeight:600, borderBottom:`1px solid ${theme.border}` }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {form.lineas.map((l, i) => (
                            <tr key={i} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>
                              <td style={{ padding:'6px 10px', color:theme.ink }}>{l.ref ? <span style={{ fontFamily:'monospace', fontWeight:600, marginRight:6 }}>{l.ref}</span> : null}{l.desc}</td>
                              <td style={{ padding:'6px 10px', color:theme.body }}>{l.cant}</td>
                              <td style={{ padding:'6px 10px', color:theme.body }}>{Number(l.precio).toFixed(2)} €</td>
                              <td style={{ padding:'6px 10px', color:theme.body }}>{l.iva}%</td>
                              <td style={{ padding:'6px 10px', color:theme.ink, fontWeight:600 }}>{(l.cant * l.precio).toFixed(2)} €</td>
                              <td style={{ padding:'6px 10px' }}>
                                <Btn size="sm" variant="ghost" title="Revisar artículo (crear o actualizar en el catálogo)" onClick={() => revisarLinea(l)}>🔍</Btn>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop:12 }}>
                  <Btn variant="primary" onClick={guardar} title="Guardar los datos extraídos como factura de proveedor">💾 Guardar como factura de proveedor</Btn>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {revisarForm && (
        <ArticuloFormModal form={revisarForm} setForm={setRevisarForm} onClose={() => setRevisarForm(null)} onSave={guardarRevisado} />
      )}
    </div>
  );
}

export function RevisarRemitentes() {
  const theme = useT();
  const [remitentes, setRemitentes] = useState(null);
  const [cargando, setCargando]     = useState(false);
  const [q, setQ]                   = useState('');
  const [error, setError]           = useState('');
  const token = () => localStorage.getItem('ri_token');

  async function cargar() {
    setCargando(true); setError(''); setRemitentes(null);
    try {
      const r = await fetch('/api/pop3/remitentes?n=100', { headers:{ Authorization:'Bearer '+token() } });
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      setRemitentes(d);
    } catch(e) { setError(e.message); }
    finally { setCargando(false); }
  }

  async function toggleWhitelist(rem) {
    const en = rem.enWhitelist;
    const url = `/api/pop3/whitelist${en ? '/'+encodeURIComponent(rem.email) : ''}`;
    await fetch(url, {
      method: en ? 'DELETE' : 'POST',
      headers:{ Authorization:'Bearer '+token(), 'Content-Type':'application/json' },
      body: en ? undefined : JSON.stringify({ email: rem.email }),
    });
    setRemitentes(rs => rs.map(r => r.email === rem.email ? { ...r, enWhitelist: !en } : r));
  }

  const filtrados = remitentes
    ? remitentes.filter(r => !q || r.email.includes(q.toLowerCase()) || r.nombre.toLowerCase().includes(q.toLowerCase()))
    : [];

  const enWL   = filtrados.filter(r => r.enWhitelist);
  const fueraWL = filtrados.filter(r => !r.enWhitelist);

  function Grupo({ titulo, items, color }) {
    if (!items.length) return null;
    return (
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:12, fontWeight:700, color, textTransform:'uppercase', marginBottom:8 }}>{titulo} ({items.length})</div>
        <div style={{ background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:10, overflow:'hidden' }}>
          {items.map((r, i) => (
            <div key={r.email} style={{ padding:'10px 14px', borderTop: i>0 ? '1px solid #f3f4f6' : 'none' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:theme.ink, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {r.nombre || r.email}
                  </div>
                  {r.nombre && <div style={{ fontSize:11, color:theme.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.email}</div>}
                </div>
                <div style={{ fontSize:11, color:theme.faint, whiteSpace:'nowrap', textAlign:'right' }}>
                  {r.ultimaFecha && <div>{r.ultimaFecha.slice(0,10)}</div>}
                  <div>{r.count} email{r.count!==1?'s':''}</div>
                </div>
                <button onClick={() => toggleWhitelist(r)} title={r.enWhitelist ? 'Quitar de whitelist' : 'Añadir a whitelist'}
                  style={{ padding:'5px 12px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:600, whiteSpace:'nowrap',
                    background: r.enWhitelist ? '#fee2e2' : '#dcfce7',
                    color:      r.enWhitelist ? '#b91c1c' : '#15803d' }}>
                  {r.enWhitelist ? '✕ Quitar' : '+ Añadir'}
                </button>
              </div>
              {r.asuntos?.length > 0 && (
                <div style={{ marginTop:5, display:'flex', flexWrap:'wrap', gap:4 }}>
                  {r.asuntos.slice(0,5).map((a,j) => (
                    <span key={j} style={{ fontSize:11, background:theme.surface2, color:theme.body, borderRadius:4, padding:'2px 7px', maxWidth:280, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'inline-block' }} title={a}>{a}</span>
                  ))}
                  {r.asuntos.length > 5 && <span style={{ fontSize:11, color:theme.faint }}>+{r.asuntos.length-5} más</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth:720 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
        <div style={{ flex:1, position:'relative' }}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Filtrar por nombre o email..."
            style={{ width:'100%', boxSizing:'border-box', padding:'9px 32px 9px 12px', border:`1px solid ${theme.border}`, borderRadius:8, fontSize:13 }} />
          {q ? <BotonLimpiar onClick={() => setQ('')} /> : null}
        </div>
        <button onClick={cargar} disabled={cargando}
          style={{ padding:'9px 18px', background:'#1a56db', color:'#fff', border:'none', borderRadius:8, fontWeight:600, fontSize:13, cursor:'pointer' }}>
          {cargando ? '⏳' : remitentes === null ? 'Cargar remitentes' : '↺ Actualizar'}
        </button>
      </div>
      {error && <div style={{ background:'#fee2e2', color:'#991b1b', borderRadius:8, padding:'10px 14px', fontSize:13, marginBottom:12 }}>⚠ {error}</div>}
      {cargando && <div style={{ color:theme.muted, fontSize:13, padding:20, textAlign:'center' }}>⏳ Cargando remitentes de los últimos 100 correos...</div>}
      {remitentes !== null && !cargando && (
        <>
          <div style={{ fontSize:12, color:theme.muted, marginBottom:14 }}>
            {filtrados.length} remitentes únicos · <b style={{ color:'#15803d' }}>{enWL.length} en whitelist</b> · <b style={{ color:theme.muted }}>{fueraWL.length} fuera</b>
            {remitentes.some(r=>r.enWhitelist) && <span style={{ marginLeft:8, color:'#92400e' }}>⚠ La whitelist está activa — solo se importan facturas de remitentes marcados</span>}
          </div>
          <Grupo titulo="En whitelist" items={enWL} color="#15803d" />
          <Grupo titulo="Resto de remitentes" items={fueraWL} color="#6b7280" />
        </>
      )}
    </div>
  );
}

// ── ESCÁNER ─────────────────────────────────────────────

function useScannerDisponible() {
  const [disponible, setDisponible] = useState(null);
  useCallback(() => {}, []);
  useState(() => {
    const token = localStorage.getItem('ri_token');
    fetch('/api/scanner/estado', { headers:{ Authorization:'Bearer '+token } })
      .then(r => r.json()).then(d => setDisponible(d.disponible)).catch(() => setDisponible(false));
  });
  return disponible;
}

// Botón compacto para escanear desde cualquier sitio — devuelve el File al padre vía onPdf
export function ScannerBtn({ onPdf }) {
  const theme = useT();
  const [estado, setEstado] = useState('idle'); // idle | escaneando | error
  const token = () => localStorage.getItem('ri_token');

  async function escanear() {
    setEstado('escaneando');
    try {
      const r = await fetch('/api/scanner/escanear', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:'Bearer '+token() },
        body: JSON.stringify({ resolucion:200, modo:'Gray', fuente:'Flatbed' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error||'Error escaneando');
      // Descargar el PDF del servidor por scanId para pasarlo como File al analizador
      const r2 = await fetch(`/api/scanner/pdf/${d.scanId}`, { headers:{ Authorization:'Bearer '+token() } });
      if (!r2.ok) throw new Error('No se pudo recuperar el PDF escaneado');
      const blob = await r2.blob();
      const file = new File([blob], `escaneado_${Date.now()}.pdf`, { type:'application/pdf' });
      setEstado('idle');
      onPdf(file);
    } catch(e) { setEstado('error'); setTimeout(() => setEstado('idle'), 3000); alert(e.message); }
  }

  return (
    <button onClick={escanear} disabled={estado==='escaneando'} title="Escanear documento con la impresora de red"
      style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 10px', border:'1.5px dashed #d1d5db', borderRadius:8, cursor: estado==='escaneando'?'wait':'pointer', fontSize:12, color: estado==='error'?'#ef4444':'#6b7280', background:theme.surface2, fontFamily:'inherit' }}>
      {estado==='escaneando' ? '⏳ Escaneando...' : '🖨️ Escanear documento'}
    </button>
  );
}

export function Scanner() {
  const theme = useT();
  const [resolucion, setResolucion] = useState('200');
  const [modo, setModo]             = useState('Gray');
  const [fuente, setFuente]         = useState('Flatbed');
  const [estado, setEstado]         = useState('idle'); // idle | escaneando | ok | error
  const [scanResult, setScanResult] = useState(null);  // { scanId, preview }
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [disponible, setDisponible] = useState(null);
  const token = () => localStorage.getItem('ri_token');

  async function cargarPdfBlob(scanId) {
    const r = await fetch(`/api/scanner/pdf/${scanId}`, { headers: { Authorization: 'Bearer ' + token() } });
    if (!r.ok) { alert('PDF no disponible o expirado'); return null; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    return url;
  }

  async function descargarPdf() {
    if (!pdfBlobUrl) return;
    const a = document.createElement('a');
    a.href = pdfBlobUrl; a.download = 'escaneado.pdf'; a.click();
  }

  useState(() => {
    fetch('/api/scanner/estado', { headers:{ Authorization:'Bearer '+token() } })
      .then(r => r.json()).then(d => setDisponible(d.disponible)).catch(() => setDisponible(false));
  });

  async function escanear() {
    setEstado('escaneando'); setScanResult(null);
    if (pdfBlobUrl) { URL.revokeObjectURL(pdfBlobUrl); setPdfBlobUrl(null); }
    try {
      const r = await fetch('/api/scanner/escanear', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:'Bearer '+token() },
        body: JSON.stringify({ resolucion: parseInt(resolucion), modo, fuente }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error||'Error al escanear');
      setScanResult(d);
      const blobUrl = await cargarPdfBlob(d.scanId);
      if (blobUrl) setPdfBlobUrl(blobUrl);
      setEstado('ok');
    } catch(e) { setEstado('error'); alert(e.message); setEstado('idle'); }
  }

  return (
    <div style={{ maxWidth:560 }}>
      {disponible === false && (
        <div style={{ background:'#fee2e2', color:'#991b1b', borderRadius:10, padding:'10px 16px', marginBottom:16, fontSize:13 }}>
          ⚠️ Escáner no detectado en la red. Comprueba que está encendido y conectado.
        </div>
      )}
      {disponible === true && (
        <div style={{ background:'#d1fae5', color:'#065f46', borderRadius:10, padding:'8px 16px', marginBottom:16, fontSize:13 }}>
          ✓ Brother DCP-7065DN disponible en 192.168.0.15
        </div>
      )}

      <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, padding:20, display:'flex', flexDirection:'column', gap:14 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>Resolución</label>
            <select value={resolucion} onChange={e=>setResolucion(e.target.value)} style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13 }}>
              <option value="100">100 dpi (rápido)</option>
              <option value="200">200 dpi (normal)</option>
              <option value="300">300 dpi (alta calidad)</option>
            </select>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>Modo</label>
            <select value={modo} onChange={e=>setModo(e.target.value)} style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13 }}>
              <option value="Gray">Escala de grises</option>
              <option value="Color">Color</option>
            </select>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>Fuente</label>
            <select value={fuente} onChange={e=>setFuente(e.target.value)} style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13 }}>
              <option value="Flatbed">Cristal (Flatbed)</option>
              <option value="ADF">Alimentador (ADF)</option>
            </select>
          </div>
        </div>

        <button onClick={escanear} disabled={estado==='escaneando'}
          style={{ padding:'10px 0', background: estado==='escaneando'?'#9ca3af':'#1a56db', color:'#fff', border:'none', borderRadius:9, fontSize:14, fontWeight:600, cursor: estado==='escaneando'?'wait':'pointer', fontFamily:'inherit' }}>
          {estado==='escaneando' ? '⏳ Escaneando...' : '🖨️ Escanear'}
        </button>

        {scanResult && (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontSize:13, color: theme===THEME.dark ? '#6ee7b7' : '#065f46', fontWeight:600 }}>✓ Escaneado correctamente</div>
              <button onClick={descargarPdf}
                style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 14px', ...pastelBtn(theme,'green'), borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                💾 Descargar PDF
              </button>
            </div>
            {pdfBlobUrl
              ? <iframe src={pdfBlobUrl} title="Vista previa PDF" style={{ width:'100%', height:700, border:`1px solid ${theme.border}`, borderRadius:8 }} />
              : <div style={{ padding:20, textAlign:'center', color:theme.faint, fontSize:13 }}>Cargando vista previa...</div>
            }
          </div>
        )}
      </div>
    </div>
  );
}
